// record-extras.jsx — In-progress course tracking, pending verification,
// and the record-detail edit sheet. Loaded after screens.jsx so it can
// hang off existing components (Sheet, Icon, CatDot, etc.).
//
// All state that needs to outlive a navigation (which sessions of a
// course you've attended, marked-completed flags) is persisted via
// localStorage — preview has no backend, but a refresh shouldn't wipe
// the user's attendance ticks.

// Hooks are called as `React.useX(...)` member calls so react-hooks/
// rules-of-hooks can see them (it only recognises `/^use[A-Z]/` and
// `React.useX`). Do not reintroduce destructured short aliases.

// ─────────────────────────────────────────────────────────────────
// Local-date helpers — DO NOT use `new Date().toISOString().slice(0,10)`
// to get "today" or to serialise a Date back to a date string. That
// converts to UTC first, which in non-UTC zones (e.g. HK +0800) silently
// shifts the date by a day around midnight, and also silently shifts
// the day-of-week. Use these helpers instead.
// ─────────────────────────────────────────────────────────────────
function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function todayLocalIso() {return ymdLocal(new Date());}
function localDateFromIso(iso) {
  const [y, m, d] = (iso || '').split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

// ─────────────────────────────────────────────────────────────────
// Session-date parsing — courses come in three shapes:
//   1. Multi-date in _raw_start_date  e.g. "23/2, 2/3, 9/3, 16/3 (Mon)"
//   2. Single ISO date in `date`      e.g. "2026-05-12"
//   3. `sessions: N` with one start   → synthesise N weekly sessions
// We return an array of { idx, iso, label, isPast } so the UI just
// renders pills.
// ─────────────────────────────────────────────────────────────────
function parseSessions(course) {
  if (!course) return [];
  const startIso = course.date;
  if (!startIso) return [];
  const startYear = Number(startIso.slice(0, 4));
  const todayIso = todayLocalIso();

  const raw = course.raw?._raw_start_date || '';
  // "23/2, 2/3, 9/3, 16/3, 23/3, 30/3, 13/4 (Mon)"  ← multi-date pattern
  const multi = raw.match(/\b\d{1,2}\/\d{1,2}\b/g);
  let isoList = [];

  if (multi && multi.length > 1) {
    let prevMonth = -1;
    let year = startYear;
    isoList = multi.map((tok) => {
      const [d, m] = tok.split('/').map((n) => parseInt(n, 10));
      // If month wraps backwards (e.g. 30/12 then 5/1), bump year.
      if (prevMonth > 0 && m < prevMonth) year += 1;
      prevMonth = m;
      return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    });
  } else {
    // Synthesise weekly sessions from the count.
    const n = Math.max(1, Math.min(20, Number(course.sessions) || 1));
    // Build from local Y/M/D so the date never gets shifted by the UTC
    // offset (`toISOString().slice(0,10)` would do that in non-UTC zones).
    const [sy, sm, sd] = startIso.split('-').map((p) => parseInt(p, 10));
    const base = new Date(sy, sm - 1, sd);
    for (let i = 0; i < n; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i * 7);
      const yy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      isoList.push(`${yy}-${mm}-${dd}`);
    }
  }

  return isoList.map((iso, idx) => {
    const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
    return {
      idx,
      iso,
      label: `${d}/${m}`,
      isPast: iso < todayIso,
      isToday: iso === todayIso
    };
  });
}

// ─────────────────────────────────────────────────────────────────
// Attendance + custom session schedules.
//
// Source of truth: Supabase `course_attendance` (per (user_id, course_id)).
// localStorage is kept as an offline read cache so first paint on a
// known device is instant — but every write goes to Supabase first, so
// switching device preserves both ticks and schedule edits.
//
// Hydration: app.jsx loads the server snapshot at sign-in and calls
// RecordExtras.hydrate({ ticks, customSessions }) before the in-progress
// screen mounts. The hydrate call writes the server snapshot into
// localStorage AND fires a 'vital:attendance-hydrated' event so any
// hooks that are already mounted re-read.
//
// Toggle path: setAttendance / setCustomSessions update React state +
// localStorage optimistically, then await the Supabase upsert. On error
// they roll back state, restore the previous localStorage value, and
// surface a toast ('無法儲存出席記錄,請稍後再試').
//
// Storage shapes:
//   ticks[courseId]           = { [idx]: 'attended' | 'missed' }
//   customSessions[courseId]  = ['YYYY-MM-DD' | 'YYYY-MM-DDTHH:MM', …]
// ─────────────────────────────────────────────────────────────────
const ATT_KEY = 'cne_attendance_v1';
function readAttendance() {
  try {return JSON.parse(localStorage.getItem(ATT_KEY) || '{}');}
  catch (e) {return {};}
}
function writeAttendance(all) {
  try {localStorage.setItem(ATT_KEY, JSON.stringify(all));}
  catch (e) {/* quota */}
}

function currentUserId() {
  // Read straight off the live supabase session each time — avoids
  // having to thread userId through every record-extras prop chain.
  // _vital_session_ is stashed by app.jsx whenever the session changes
  // (see app.jsx reloadUserData), so this is sync and cheap.
  return window._vital_session_?.user?.id || null;
}

function currentLang() {
  return window.__VITAL_LANG__ || 'zh';
}

function showAttendanceError() {
  const lang = currentLang();
  const msg = lang === 'zh'
    ? '無法儲存出席記錄,請稍後再試'
    : 'Could not save attendance. Please try again.';
  if (typeof window.showVitalToast === 'function') window.showVitalToast(msg);
}

function useAttendance() {
  const [all, setAll] = React.useState(readAttendance);
  // allRef keeps a non-stale view of the current state so the setter can
  // compute next-values OUTSIDE the React updater function. React 18 may
  // queue updater functions for the next render — relying on them to fill
  // closure-scoped `let`s and then awaiting Supabase against those values
  // is racy (the await can land before the updater has run, and the
  // upsert ends up with `undefined` ticks → server-side clear).
  const allRef = React.useRef(all);
  React.useEffect(() => { allRef.current = all; }, [all]);

  React.useEffect(() => {
    const handler = () => {
      const fresh = readAttendance();
      allRef.current = fresh;
      setAll(fresh);
    };
    // 'attendance-hydrated' fires once from app.jsx after the server snapshot
    // lands. 'attendance-changed' fires on every local set() below so that
    // OTHER hook instances (e.g. the reminder-effect consumer in
    // InProgressSection, separate from each card's instance) re-sync from
    // localStorage and re-render with the just-written value.
    window.addEventListener('vital:attendance-hydrated', handler);
    window.addEventListener('vital:attendance-changed', handler);
    return () => {
      window.removeEventListener('vital:attendance-hydrated', handler);
      window.removeEventListener('vital:attendance-changed', handler);
    };
  }, []);

  const set = async (courseId, idx, status) => {
    const userId = currentUserId();
    // Compute prev/next synchronously from the ref — no race with React's
    // updater scheduling.
    const prevSnapshot = allRef.current;
    const prevCourseTicks = prevSnapshot[courseId] || {};
    const nextCourseTicks = { ...prevCourseTicks };
    if (!status) delete nextCourseTicks[idx]; else nextCourseTicks[idx] = status;
    const nextAll = { ...prevSnapshot, [courseId]: nextCourseTicks };

    allRef.current = nextAll;
    setAll(nextAll);
    writeAttendance(nextAll);
    // Notify sibling hook instances (e.g. InProgressSection's reminder
    // effect) so they re-read storage and re-render with the new tick.
    window.dispatchEvent(new Event('vital:attendance-changed'));

    // Guest mode — localStorage is the only store, done.
    if (!userId || !window.DB?.upsertCourseAttendance) return;

    // upsertCourseAttendance already catches its own throws, but a defensive
    // try/catch here guards against unrelated faults (e.g. window.DB getting
    // monkey-patched mid-flight) so rollback is the LAST thing the user sees.
    let result;
    try {
      result = await window.DB.upsertCourseAttendance(userId, courseId, {
        ticks: nextCourseTicks,
      });
    } catch (e) {
      result = { error: e, missingTable: false };
    }
    if (result?.error) {
      // If the table isn't deployed yet, treat as graceful degradation:
      // keep the optimistic localStorage state and stay silent. This is
      // the same UX as the pre-feature build — no toast spam during the
      // window between client deploy and SQL apply. The console.warn
      // inside upsertCourseAttendance is enough for the engineer.
      if (result.missingTable) return;
      allRef.current = prevSnapshot;
      setAll(prevSnapshot);
      writeAttendance(prevSnapshot);
      window.dispatchEvent(new Event('vital:attendance-changed'));
      showAttendanceError();
    }
  };
  return [all, set];
}

// ─────────────────────────────────────────────────────────────────
// Custom session dates — let the user override / add / remove the
// session schedule for a course. Used when the catalog data is
// missing or wrong, or when the user is tracking a course they
// added themselves. Keyed by courseId; an empty array still counts
// as "user has taken control" so we don't fall back to the synthetic
// schedule.
// ─────────────────────────────────────────────────────────────────
const SESSIONS_KEY = 'cne_session_dates_v1';
function readCustomSessions() {
  try {return JSON.parse(localStorage.getItem(SESSIONS_KEY) || '{}');}
  catch (e) {return {};}
}
function writeCustomSessions(all) {
  try {localStorage.setItem(SESSIONS_KEY, JSON.stringify(all));}
  catch (e) {/* quota */}
}
function useCustomSessions() {
  const [all, setAll] = React.useState(readCustomSessions);
  const allRef = React.useRef(all);
  React.useEffect(() => { allRef.current = all; }, [all]);

  React.useEffect(() => {
    const handler = () => {
      const fresh = readCustomSessions();
      allRef.current = fresh;
      setAll(fresh);
    };
    // 'attendance-hydrated' fires once from app.jsx after the server snapshot
    // lands. 'custom-sessions-changed' fires on every local set() below so
    // sibling hook instances (the reminder-effect consumer in
    // InProgressSection lives apart from each card's instance) re-read
    // localStorage and re-render with the latest override.
    window.addEventListener('vital:attendance-hydrated', handler);
    window.addEventListener('vital:custom-sessions-changed', handler);
    return () => {
      window.removeEventListener('vital:attendance-hydrated', handler);
      window.removeEventListener('vital:custom-sessions-changed', handler);
    };
  }, []);

  // dates may be null (= "delete the override, fall back to default") or
  // an array of ISO date strings (auto-sorted).
  const set = async (courseId, dates) => {
    const userId = currentUserId();
    const prevSnapshot = allRef.current;
    let nextDates;  // null = cleared
    let nextAll;
    if (dates === null || dates === undefined) {
      nextAll = { ...prevSnapshot };
      delete nextAll[courseId];
      nextDates = null;
    } else {
      nextDates = [...dates].filter(Boolean).sort();
      nextAll = { ...prevSnapshot, [courseId]: nextDates };
    }

    allRef.current = nextAll;
    setAll(nextAll);
    writeCustomSessions(nextAll);
    // Notify sibling hook instances (e.g. the reminder effect in
    // InProgressSection) so they pick up the override and re-render.
    window.dispatchEvent(new Event('vital:custom-sessions-changed'));

    if (!userId || !window.DB?.upsertCourseAttendance) return;
    let result;
    try {
      result = await window.DB.upsertCourseAttendance(userId, courseId, {
        customSessions: nextDates,
      });
    } catch (e) {
      result = { error: e, missingTable: false };
    }
    if (result?.error) {
      if (result.missingTable) return;
      allRef.current = prevSnapshot;
      setAll(prevSnapshot);
      writeCustomSessions(prevSnapshot);
      window.dispatchEvent(new Event('vital:custom-sessions-changed'));
      showAttendanceError();
    }
  };
  return [all, set];
}

// Hydration entry point — called by app.jsx with the server snapshot.
//
// Merge strategy (a conscious decision, not undefined behaviour):
//
//   ticks            — PER-INDEX merge, server wins on conflict.
//                      For each (courseId, sessionIdx), the value the
//                      server has is the last successfully-upserted
//                      value, so it wins if both sides claim that
//                      index. Local-only indexes (offline writes,
//                      pre-feature ticks) are preserved AND pushed
//                      back up so they reach other devices.
//
//   custom_sessions  — WHOLE-RECORD server wins. The schedule is a
//                      sequence with no per-entry identity; merging
//                      two arrays of session dates yields nonsense.
//                      A user who edits the schedule on the losing
//                      device sees the server's schedule after sync
//                      and can re-edit if they care.
//
// Error path: if the server fetch failed, leave localStorage entirely
// alone — clobbering with an incorrect empty would silently wipe the
// user's on-device ticks.
//
// After the merge we fire 'vital:attendance-hydrated' so any mounted
// hooks re-read state without waiting for a remount.
//
// Note: this resolves the multi-device case without per-tick
// timestamps. If we ever need true last-write-wins, add a child table
// keyed (user_id, course_id, session_idx) with its own updated_at and
// switch the merge to compare timestamps per index.
function hydrateAttendance(serverData) {
  if (serverData?.error) {
    console.warn('[Vital attendance] hydrate skipped — server fetch failed:',
      serverData.error?.message || serverData.error);
    // Still fire the event so hooks attached after sign-in re-read
    // whatever localStorage already has (the offline fallback).
    try { window.dispatchEvent(new CustomEvent('vital:attendance-hydrated')); } catch (_) {}
    return;
  }
  const serverTicks  = serverData?.ticks          || {};
  const serverCustom = serverData?.customSessions || {};
  const localTicks   = readAttendance();
  const localCustom  = readCustomSessions();

  // ── ticks: per-index merge, server-wins-on-conflict ───────────────────
  const mergedTicks  = {};
  const ticksToPush  = new Map();   // courseId → fully-merged ticks
  const allTickIds   = new Set([...Object.keys(localTicks), ...Object.keys(serverTicks)]);
  for (const cid of allTickIds) {
    const l = localTicks[cid]  || {};
    const s = serverTicks[cid] || {};
    // Spread order = server wins on overlapping index keys.
    const merged = { ...l, ...s };
    if (Object.keys(merged).length) mergedTicks[cid] = merged;
    // Has local something the server didn't see? If so, the merged
    // result needs to go back up so other devices learn about it.
    const localOnly = Object.keys(l).some(k => !(k in s));
    if (localOnly) ticksToPush.set(cid, merged);
  }

  // ── custom_sessions: whole-record server-wins ─────────────────────────
  const mergedCustom  = { ...localCustom, ...serverCustom };
  const customToPush  = new Map();  // courseId → array (only when server didn't have it)
  for (const [cid, arr] of Object.entries(localCustom)) {
    if (!Array.isArray(serverCustom[cid]) && Array.isArray(arr) && arr.length) {
      customToPush.set(cid, arr);
    }
  }

  // Persist locally first (instant paint), then reconcile up. Hydrate
  // must not block the UI on a slow network — every push is fire-and-
  // forget; a failure just means the next pill toggle will retry.
  writeAttendance(mergedTicks);
  writeCustomSessions(mergedCustom);
  try {
    window.dispatchEvent(new CustomEvent('vital:attendance-hydrated'));
  } catch (_) { /* old browsers */ }

  const userId = currentUserId();
  if (userId && window.DB?.upsertCourseAttendance) {
    const pushIds = new Set([...ticksToPush.keys(), ...customToPush.keys()]);
    if (pushIds.size) {
      console.log('[Vital attendance] reconciling', pushIds.size,
        'course(s) — local had data the server had not seen');
      for (const cid of pushIds) {
        const patch = {};
        if (ticksToPush.has(cid))  patch.ticks          = ticksToPush.get(cid);
        if (customToPush.has(cid)) patch.customSessions = customToPush.get(cid);
        window.DB.upsertCourseAttendance(userId, cid, patch).catch(() => {});
      }
    }
  }
}

// Called on sign-out so the next user (or guest) doesn't see stale data.
function clearAttendanceCache() {
  try { localStorage.removeItem(ATT_KEY); } catch (_) {}
  try { localStorage.removeItem(SESSIONS_KEY); } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent('vital:attendance-hydrated'));
  } catch (_) {}
}

// Build the session list a course should render, layering custom dates
// on top of parseSessions' default. Also normalises attendance keys so
// flipping between default and custom doesn't lose ticks.
//
// Storage entries can be either "YYYY-MM-DD" (date only) or
// "YYYY-MM-DDTHH:MM" (date + time). We split them into iso/time on
// the way out so the UI can render both bits.
function normaliseSessionEntry(entry) {
  if (entry == null) return null;
  if (typeof entry !== 'string') return null;
  if (entry.includes('T')) {
    const [d, t] = entry.split('T');
    return { iso: d, time: (t || '').slice(0, 5) || null };
  }
  return { iso: entry, time: null };
}
function serialiseSessionEntry({ iso, time }) {
  if (!iso) return null;
  return time ? `${iso}T${time}` : iso;
}

// Pull a 24-hour "HH:MM" start time out of the catalog's free-text time_text
// (course.time) so the schedule editor can seed the course's REAL time instead
// of a hardcoded 14:00. Every live value starts with a 24h HH:MM — ranges like
// "19:00-21:00", multi-block "Day 1: 19:00-21:00 / Day 2: …", or "(lessons 1-3)"
// suffixes — so we take the first HH:MM. am/pm suffixes (defensive; not seen in
// live data) fold into 24h. Returns null when nothing parseable is present
// (e.g. "TBA"), so callers can fall back.
function parseTimeTextStart(timeText) {
  if (!timeText || typeof timeText !== 'string') return null;
  const m = timeText.match(/(\d{1,2}):(\d{2})\s*([ap])\.?m\.?/i) ||
            timeText.match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (isNaN(h) || isNaN(min) || h > 23 || min > 59) return null;
  const mer = (m[3] || '').toLowerCase();
  if (mer === 'p' && h < 12) h += 12;
  if (mer === 'a' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

// Derive the SCALAR schedule columns (start_date / end_date / sessions /
// time_text) from a canonical sessions_list (array of "YYYY-MM-DD[THH:MM]"
// strings) so the cards/queries that read those scalars keep working when a
// course carries a full per-session list. Returns null for an empty list.
//
// KEEP IN SYNC with the SQL derivation in approve_submission / apply_report
// (supabase-migration-crowdsource-rpc.sql) — both must agree on the rule:
//   start_date = first date, end_date = last date (null if single session),
//   sessions = count, time_text = the shared time IFF every session shares one.
function deriveScheduleFields(sessionsList) {
  const entries = (sessionsList || []).map(normaliseSessionEntry).filter(Boolean);
  if (!entries.length) return null;
  const isos = entries.map((e) => e.iso).filter(Boolean).sort();
  const times = entries.map((e) => e.time).filter(Boolean);
  const allTimed = times.length === entries.length;
  const uniqTimes = Array.from(new Set(times));
  return {
    start_date: isos[0] || null,
    end_date: isos.length > 1 ? isos[isos.length - 1] : null,
    sessions: entries.length,
    time_text: allTimed && uniqTimes.length === 1 ? uniqTimes[0] : null,
  };
}

// One-line human summary of a sessions_list for the "schedule set" chip in the
// submit / log / report flows. Empty list → null so callers show a placeholder.
function summariseSessionList(sessionsList, lang) {
  const entries = (sessionsList || [])
    .map(normaliseSessionEntry)
    .filter(Boolean)
    .sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0));
  if (!entries.length) return null;
  const fmt = (iso) => {
    const [, m, d] = (iso || '').split('-').map((n) => parseInt(n, 10));
    if (!m || !d) return iso;
    return lang === 'zh' ? `${m}月${d}日` : `${m}/${d}`;
  };
  const first = entries[0];
  const n = entries.length;
  const timed = first.time ? ` · ${first.time}` : '';
  if (n === 1) return `${fmt(first.iso)}${timed}`;
  return lang === 'zh'
    ? `${fmt(first.iso)}起 · 共 ${n} 節`
    : `From ${fmt(first.iso)} · ${n} sessions`;
}

// Has the user's effective schedule actually diverged from the course's default?
// Both args are RESOLVED session arrays (resolveSessions output — {iso, time}).
//
// The subtle part is TIME. A catalog row often has no structured per-session
// time: it carries only a start date (+ free-text time_text like "19:00-21:00"
// that the structured schedule deliberately can't parse), so its default
// sessions are date-only. The schedule editor, however, always stamps a default
// time (14:00) onto every session — so merely opening + saving the editor, with
// no real edit, would otherwise produce an override that "differs" purely by
// that injected time and light up the share button. To avoid that false
// positive we compare DATES ONLY when the default has no structured time; once
// the catalog carries real per-session times (a sessions_list) those times
// count, so a genuine time correction still registers.
//
// Comparing the resolved {iso,time} (not raw serialised strings) also makes
// this robust to format drift — both inputs already passed through
// normaliseSessionEntry (time sliced to HH:MM, "T" handled).
function sameSchedule(effective, def) {
  const defHasTimes = (def || []).some((s) => s && s.time);
  const key = (s) => defHasTimes ? `${s.iso || ''}T${s.time || ''}` : `${s.iso || ''}`;
  const a = (effective || []).map(key).sort();
  const b = (def || []).map(key).sort();
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

function resolveSessions(course, customSessions) {
  const todayIso = todayLocalIso();
  const override = customSessions && customSessions[course.id];
  let list;
  if (Array.isArray(override)) {
    list = override.map(normaliseSessionEntry).filter(Boolean);
  } else if (Array.isArray(course.sessionsList) && course.sessionsList.length) {
    // Canonical per-session list from the catalog (cne_courses.sessions_list),
    // used when the user has no personal attendance override. Priority:
    //   user custom_sessions → course.sessionsList → synthesise from count.
    list = course.sessionsList.map(normaliseSessionEntry).filter(Boolean);
  } else {
    list = parseSessions(course).map((s) => ({ iso: s.iso, time: null }));
  }
  // GUARANTEE at least one session — every in-progress course must
  // surface something pickable, even if the catalog has no schedule.
  // Falls back to the course's start date (or today as last resort).
  if (list.length === 0) {
    list = [{ iso: course.date || todayIso, time: null }];
  }
  // Stable sort by iso+time so an editor that adds out-of-order rows
  // still presents them chronologically.
  list = [...list].sort((a, b) => {
    const ka = `${a.iso || ''}T${a.time || '00:00'}`;
    const kb = `${b.iso || ''}T${b.time || '00:00'}`;
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  return list.map((entry, idx) => {
    const [y, m, d] = (entry.iso || '').split('-').map((n) => parseInt(n, 10));
    return {
      idx,
      iso: entry.iso,
      time: entry.time || null,
      label: d && m ? `${d}/${m}` : entry.iso,
      timeLabel: entry.time || null,
      isPast: entry.iso && entry.iso < todayIso,
      isToday: entry.iso === todayIso,
      isCustom: Array.isArray(override)
    };
  });
}

// ─────────────────────────────────────────────────────────────────
// CLASS REMINDERS — now SERVER-SIDE push.
//
// Class ("you have a class soon") reminders were retired from the on-device
// @capacitor/local-notifications path and moved to backend APNs push: the
// `notify-cron` Edge Function's 'class' sweep reads the user's stored
// sessionReminders pref (enabled + leadDays) and sends server-side, so a
// reminder fires even if the user never reopens the app and respects the
// per-device-independent push master. The local scheduler is gone; only a
// one-time launch cleanup remains (LocalAPI.cancelByKind(SESSION_REMINDER_KIND)
// in app.jsx) to clear any reminders scheduled by older app versions.
// ─────────────────────────────────────────────────────────────────

// Cycle: blank → attended → missed → blank
const NEXT_STATUS = { undefined: 'attended', attended: 'missed', missed: undefined };

// ─────────────────────────────────────────────────────────────────
// <SessionPill> — one round-rect button per session date
// ─────────────────────────────────────────────────────────────────
function SessionPill({ session, status, onCycle }) {
  // Tone resolution
  let bg = 'var(--surface)';
  let color = 'var(--fg-2)';
  let border = '0.5px solid var(--border)';
  let icon = null;
  if (status === 'attended') {
    bg = 'color-mix(in oklab, var(--success) 18%, var(--surface))';
    color = 'var(--success)';
    border = '0.5px solid color-mix(in oklab, var(--success) 40%, transparent)';
    icon = <Icon name="check" size={11} stroke={2.4} />;
  } else if (status === 'missed') {
    bg = 'color-mix(in oklab, var(--danger) 12%, var(--surface))';
    color = 'var(--danger)';
    border = '0.5px solid color-mix(in oklab, var(--danger) 40%, transparent)';
    icon = <Icon name="close" size={11} stroke={2.4} />;
  } else if (session.isPast) {
    // Past unmarked — nudge with dashed accent
    bg = 'color-mix(in oklab, var(--accent) 8%, var(--surface))';
    color = 'var(--accent)';
    border = '0.5px dashed color-mix(in oklab, var(--accent) 45%, transparent)';
  } else if (!session.isPast) {
    // Future — muted
    bg = 'var(--bg-2)';
    color = 'var(--fg-3)';
    border = '0.5px solid var(--border)';
  }

  const title = status === 'attended' ? '已上' : status === 'missed' ? '冇上' :
  session.isPast ? '點擊標記' : '未開始';

  return (
    <button
      type="button"
      onClick={onCycle}
      title={title}
      aria-label={`${session.label} — ${title}`}
      className="session-pill"
      style={{
        background: bg, color, border,
        appearance: 'none', cursor: 'pointer',
        padding: '7px 11px', borderRadius: 999,
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 12.5, fontWeight: 500,
        fontFamily: 'var(--font-mono)', letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
        transition: 'transform .12s ease, background .15s ease, color .15s ease, border-color .15s ease'
      }}>
      {icon}
      <span>{session.label}{session.timeLabel ? ` · ${session.timeLabel}` : ''}</span>
      {session.isToday &&
      <span style={{
        width: 5, height: 5, borderRadius: '50%',
        background: 'currentColor', marginLeft: 1
      }} />
      }
    </button>);

}

// ─────────────────────────────────────────────────────────────────
// <InProgressCourseCard> — one enrolled course with session tracker
// ─────────────────────────────────────────────────────────────────
function InProgressCourseCard({ course, lang, attendance, onCycle, onSelect, onViewDetail, onEditRecord, onMarkComplete }) {
  const [customAll, setCustom] = useCustomSessions();
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [shareOpen, setShareOpen] = React.useState(false);
  const [completing, setCompleting] = React.useState(false);
  const markBtnRef = React.useRef(null);
  const wasAllMarkedRef = React.useRef(false);

  const sessions = React.useMemo(
    () => resolveSessions(course, customAll),
    [course?.id, customAll]
  );
  const my = attendance[course.id] || {};
  const attended = sessions.filter((s) => my[s.idx] === 'attended').length;
  const missed = sessions.filter((s) => my[s.idx] === 'missed').length;
  const total = sessions.length || 1;
  const pct = Math.round(attended / total * 100);
  const nextSession = sessions.find((s) => !s.isPast && !my[s.idx]);
  const today = todayLocalIso();
  const dToNext = nextSession ?
  Math.max(0, Math.round((localDateFromIso(nextSession.iso) - localDateFromIso(today)) / 86400000)) :
  null;
  const hasOverride = Array.isArray(customAll[course.id]);
  // 學分待補 — only manual records carry PTSTBD:1; a catalog enrolment takes
  // its points from the catalogue row, so there is nothing pending there.
  const pointsPending = !!(course.isManual && course._record && isRecordPointsPending(course._record));

  // "Share my edited schedule to the community" eligibility. The personal edit
  // lives in custom_sessions (per-user); contributing it must go through the
  // course_reports review pipeline (NOT a direct catalog write) and only makes
  // sense for CATALOG courses (manual records have no shared row — those use
  // the co-submit-from-log path instead). overrideList/canonicalList feed the
  // share sheet's payload + report type below.
  const overrideList = Array.isArray(customAll[course.id]) ? customAll[course.id] : null;
  const canonicalList = Array.isArray(course.sessionsList) ? course.sessionsList : null;
  // Only surface the shortcut when the user has ACTUALLY changed the schedule —
  // their effective `sessions` must differ from what the course shows with no
  // override (see sameSchedule: dates always count, times only once the catalog
  // has structured per-session times). This means a no-op editor save — which
  // stamps the editor's default time onto the same dates — does NOT light up
  // the button. (Was: raw-string compare vs course.sessionsList, which lit up
  // whenever the catalog stored only scalar dates or a differently-formatted
  // list.)
  const defaultSessions = resolveSessions(course, null);
  // True only when the saved override is a REAL change to the schedule. Drives
  // BOTH the "自訂" badge and the share shortcut so they never disagree — a
  // no-op override (editor's default time on the same dates) shows neither.
  const scheduleChanged = hasOverride && !sameSchedule(sessions, defaultSessions);
  const canShareSchedule = !course.isManual && !!course.id && !String(course.id).startsWith('manual-')
    && scheduleChanged;

  // All session markers have a non-blank status (attended or missed) →
  // ready to be marked complete. Empty session lists don't count.
  const allMarked = sessions.length > 0 && sessions.every((s) => !!my[s.idx]);

  // When `allMarked` first flips true, surrounding sections may collapse
  // (e.g. UpcomingCalendarSection returns null once nothing is upcoming),
  // shifting the page enough that the new 標記為已完成 button lands
  // outside the user's viewport — looks like the action "disappeared".
  // Scroll it back into view so the next step is always visible.
  React.useEffect(() => {
    if (allMarked && !wasAllMarkedRef.current && markBtnRef.current) {
      // Defer until after the layout has settled (calendar/section
      // unmount happens in the same paint).
      const el = markBtnRef.current;
      requestAnimationFrame(() => {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        catch (_) { /* old webviews — silently skip */ }
      });
    }
    wasAllMarkedRef.current = allMarked;
  }, [allMarked]);

  const saveSessions = (entries) => {
    // entries: [{ iso, time }]  — null entries skipped
    const serialised = entries.
    map(serialiseSessionEntry).
    filter(Boolean);
    setCustom(course.id, serialised);
  };
  const resetToDefault = () => setCustom(course.id, null);

  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <button
        type="button"
        onClick={() => {
          // Manual records: parent already wired onSelect → record editor.
          // Catalog courses: open an action picker so the user can choose
          //   • 編輯紀錄  — edit the matching record (or log it if missing)
          //   • 查看詳情  — go to the catalog detail page
          if (course.isManual) {
            onSelect && onSelect();
          } else {
            setPickerOpen(true);
          }
        }}
        className="rx-course-head"
        style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          appearance: 'none', background: 'transparent', border: 0,
          padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left',
          cursor: 'pointer', width: '100%'
        }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5, flexWrap: 'wrap' }}>
            <TopicPill topic={course.topic} label={L(course.topicLabel, lang)} />
            {/* 學分待補 — this course was captured before it ran (poster or
                course link) and its CNE points weren't published yet. Visible
                here, not just at completion time, because the whole risk is
                that a 0-point record ages into 已完成 unnoticed. */}
            {pointsPending &&
            <span className="chip" style={{
              background: 'color-mix(in oklch, var(--warning) 12%, transparent)',
              color: 'var(--warning)',
              border: '0.5px solid color-mix(in oklch, var(--warning) 35%, transparent)'
            }}>
                <Icon name="shield" size={10} stroke={1.9} />
                {L(STR.pointsTbdChip, lang)}
              </span>
            }
            {nextSession &&
            <span className="chip" style={{
              background: dToNext === 0 ? 'var(--accent-soft)' : 'transparent',
              color: dToNext === 0 ? 'var(--accent)' : 'var(--fg-2)',
              border: dToNext === 0 ? '0' : '0.5px solid var(--border)'
            }}>
                <Icon name="clock" size={10} stroke={1.8} />
                {dToNext === 0 ?
              lang === 'zh' ? '今日上堂' : 'Class today' :
              lang === 'zh' ? `${dToNext} 日後` : `in ${dToNext}d`}
              </span>
            }
          </div>
          <div className="h-row" style={{ fontSize: 14.5, lineHeight: 1.3 }}>
            {L(course.title, lang)}
          </div>
          <div className="mono muted-2" style={{ fontSize: 10.5, letterSpacing: '0.08em', marginTop: 4, textTransform: 'uppercase' }}>
            {L(course.providerShort, lang)} · {sessions.length} {lang === 'zh' ? '節' : 'sessions'}
            {scheduleChanged &&
            <span style={{ marginLeft: 6, color: 'var(--accent)' }}>
                · {lang === 'zh' ? '自訂' : 'custom'}
              </span>
            }
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div className="serif" style={{ fontSize: 22, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {attended}<span className="muted-2" style={{ fontSize: 13 }}>/{total}</span>
          </div>
          <div className="mono muted-2" style={{ fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase', marginTop: 3 }}>
            {lang === 'zh' ? '已上' : 'attended'}
          </div>
        </div>
      </button>

      {/* Progress bar */}
      <div className="progress-bar" style={{ height: 4 }}>
        <div style={{ width: pct + '%', background: 'var(--success)' }} />
      </div>

      {/* Session pills + a single edit icon button at the row's end. The
           edit button opens a full dialog (固定日期 / 不固定日期). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {sessions.map((s) =>
        <SessionPill
          key={s.idx}
          session={s}
          status={my[s.idx]}
          onCycle={() => onCycle(course.id, s.idx, NEXT_STATUS[my[s.idx]])} />

        )}
        <button
          type="button"
          onClick={() => setEditorOpen(true)}
          aria-label={lang === 'zh' ? '編輯日期' : 'Edit session dates'}
          title={lang === 'zh' ? '編輯日期' : 'Edit session dates'}
          style={{
            appearance: 'none', cursor: 'pointer',
            width: 30, height: 30, borderRadius: 999,
            background: 'var(--surface)',
            border: '0.5px solid var(--border)',
            color: 'var(--fg-2)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: 0
          }}>
          <Icon name="edit" size={13} stroke={1.7} />
        </button>
      </div>

      {/* Contribute the edited schedule to the shared catalogue — framed as a
           correction so it routes through admin review, not a direct write. */}
      {canShareSchedule &&
      <button
        type="button"
        onClick={() => setShareOpen(true)}
        style={{
          appearance: 'none', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          alignSelf: 'flex-start', padding: '6px 4px',
          background: 'transparent', border: 0,
          color: 'var(--accent)', font: 'inherit', fontSize: 12
        }}>
        <Icon name="upload" size={13} stroke={1.7} />
        {lang === 'zh' ? '課表有錯?回報正確時間到社群' : 'Schedule wrong? Suggest a correction'}
      </button>
      }

      {/* Mark-as-complete CTA — appears once every session has been
           ticked off (attended or missed). Pushes the record into the
           已完成紀錄 timeline; for catalog courses this also auto-
           unenrolls them so they stop appearing in 進行中. */}
      {allMarked && onMarkComplete &&
      <button
        ref={markBtnRef}
        type="button"
        disabled={completing}
        aria-busy={completing || undefined}
        onClick={async () => {
          if (completing) return;
          setCompleting(true);
          try {await onMarkComplete(sessions);} finally
          {setCompleting(false);}
        }}
        style={{
          appearance: 'none',
          cursor: completing ? 'wait' : 'pointer',
          padding: '11px 14px', borderRadius: 10,
          background: 'var(--success)',
          border: 0, color: '#fff',
          font: 'inherit', fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          opacity: completing ? 0.7 : 1,
          transition: 'opacity .15s, transform .12s'
        }}>
          {completing
            ? <Spinner size={15} color="#fff" />
            : <Icon name="check" size={15} stroke={2.4} />}
          {completing ?
        lang === 'zh' ? '處理中…' : 'Marking…' :
        lang === 'zh' ? '標記為已完成' : 'Mark as completed'}
        </button>
      }

      {/* Footer summary */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 11.5, color: 'var(--fg-2)',
        paddingTop: 8, borderTop: '0.5px solid var(--border)'
      }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)' }} />
            {attended} {lang === 'zh' ? '已上' : 'attended'}
          </span>
          {missed > 0 &&
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)' }} />
              {missed} {lang === 'zh' ? '冇上' : 'missed'}
            </span>
          }
        </div>
        <span className="muted-2" style={{ fontSize: 10.5 }}>
          {lang === 'zh' ? '點擊日期標記' : 'Tap a date to mark'}
        </span>
      </div>

      {editorOpen &&
      <SessionScheduleDialog
        course={course}
        lang={lang}
        sessions={sessions}
        hasOverride={hasOverride}
        onClose={() => setEditorOpen(false)}
        onSave={(entries) => {saveSessions(entries);setEditorOpen(false);}}
        onReset={() => {resetToDefault();setEditorOpen(false);}} />

      }

      {pickerOpen &&
      <CourseActionPickerDialog
        course={course}
        lang={lang}
        onClose={() => setPickerOpen(false)}
        onEditRecord={() => {setPickerOpen(false);onEditRecord && onEditRecord();}}
        onViewDetail={() => {setPickerOpen(false);onViewDetail && onViewDetail();}} />

      }

      {/* Share-schedule shortcut: opens the standard report sheet preset to the
           sessions_list field, pre-filled with the user's edited schedule. Goes
           through course_reports → admin review like any other correction.
           ReportCourseSheet lives in screens.jsx (loaded after this file) but is
           a global by render time, so guard with a typeof check. */}
      {shareOpen && typeof ReportCourseSheet === 'function' &&
      <ReportCourseSheet
        lang={lang}
        course={course}
        initial={{
          fieldPath: 'sessions_list',
          reportType: canonicalList ? 'incorrect_field' : 'missing_field',
          suggestedValue: JSON.stringify(overrideList),
        }}
        onClose={() => setShareOpen(false)}
        onSubmitted={() => {}} />
      }
    </div>);

}

// ─────────────────────────────────────────────────────────────────
// <CourseActionPickerDialog> — small action sheet shown when a user
// taps a CATALOG course card in 正在參與的課程. Lets them pick
// between editing the linked record and viewing the catalog detail
// page. Manual records skip this picker entirely (no catalog page
// exists for them, so we route straight to the record editor).
// ─────────────────────────────────────────────────────────────────
function CourseActionPickerDialog({ course, lang, onClose, onEditRecord, onViewDetail }) {
  return (
    <Sheet open={true} onClose={onClose} title={L(course.title, lang)}>
      <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button type="button"
        onClick={onEditRecord}
        style={{
          appearance: 'none', cursor: 'pointer', textAlign: 'left',
          display: 'flex', gap: 14, alignItems: 'center', padding: 14,
          borderRadius: 12, background: 'var(--surface)',
          border: '0.5px solid var(--border)',
          font: 'inherit', color: 'inherit'
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 12,
            background: 'var(--accent-soft)', color: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Icon name="edit" size={18} stroke={1.8} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="h-row" style={{ fontSize: 14 }}>
              {lang === 'zh' ? '編輯紀錄' : 'Edit record'}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.4 }}>
              {lang === 'zh' ? '記錄出席、上傳證書、加入備註及圖片' : 'Track attendance, upload certificates, add notes & images'}
            </div>
          </div>
          <Icon name="chev-r" size={15} color="var(--fg-3)" stroke={1.7} />
        </button>

        <button type="button"
        onClick={onViewDetail}
        style={{
          appearance: 'none', cursor: 'pointer', textAlign: 'left',
          display: 'flex', gap: 14, alignItems: 'center', padding: 14,
          borderRadius: 12, background: 'var(--surface)',
          border: '0.5px solid var(--border)',
          font: 'inherit', color: 'inherit'
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 12,
            background: 'var(--primary-soft-2)', color: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Icon name="book" size={18} stroke={1.8} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="h-row" style={{ fontSize: 14 }}>
              {lang === 'zh' ? '查看詳情' : 'View course detail'}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.4 }}>
              {lang === 'zh' ? '檢視課程簡介、時間表及主辦機構' : 'See description, schedule and provider info'}
            </div>
          </div>
          <Icon name="chev-r" size={15} color="var(--fg-3)" stroke={1.7} />
        </button>
      </div>
    </Sheet>);

}

// ─────────────────────────────────────────────────────────────────
// <SessionScheduleDialog> — full editor for a course's session
// schedule. Two modes:
//   • 固定日期 (Fixed) — total count + days-of-week + per-day time,
//     auto-generates date list from an anchor start date.
//   • 不固定日期 (Custom) — manual list of date+time rows.
// Both modes commit a list of normalised { iso, time } entries.
// ─────────────────────────────────────────────────────────────────
function SessionScheduleDialog({ course, lang, sessions, hasOverride, onClose, onSave, onReset }) {
  const DOW_LABELS = lang === 'zh' ?
  ['日', '一', '二', '三', '四', '五', '六'] :
  ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const todayIso = todayLocalIso();
  const anchorDefault = sessions[0]?.iso || course.date || todayIso;
  // Default time for newly-picked sessions: the catalog's real time_text
  // ("19:00-21:00" → "19:00") when available, else 14:00. Seeding the right
  // time means opening the editor and saving without edits no longer rewrites a
  // 19:00 course to a bogus 14:00 (and the share button stays quiet — see
  // sameSchedule).
  const defaultTime = parseTimeTextStart(course?.time) || '14:00';

  const [mode, setMode] = React.useState(hasOverride && sessions.some((s) => !s.time) ? 'custom' : 'fixed');
  // — Fixed mode state ————————————————————————————————————————————
  const initialCount = Math.max(1, sessions.length || course.sessions || 1);
  const [count, setCount] = React.useState(String(initialCount));
  const [anchor, setAnchor] = React.useState(anchorDefault);
  // Derive selected days-of-week + per-day default time from incoming
  // sessions if possible; otherwise fall back to the anchor's day-of-week.
  const initialFromSessions = React.useMemo(() => {
    const map = {};
    for (const s of sessions) {
      if (!s.iso) continue;
      // Build the Date from y/m/d explicitly so timezone never shifts
      // the day-of-week (the same bug that previously made selecting
      // 每周二 generate dates that read as 每周一 in the preview).
      const dow = localDateFromIso(s.iso).getDay();
      if (!map[dow]) map[dow] = s.time || defaultTime;
    }
    if (Object.keys(map).length) return map;
    const anchorDow = localDateFromIso(anchorDefault).getDay();
    return { [anchorDow]: defaultTime };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [dayTimes, setDayTimes] = React.useState(initialFromSessions); // { 1: '14:00', 3: '20:00' }

  // — Custom mode state ————————————————————————————————————————————
  const [items, setItems] = React.useState(
    sessions.length ?
    sessions.map((s) => ({ date: s.iso, time: s.time || '' })) :
    [{ date: anchorDefault, time: '' }]
  );

  // — Fixed-mode preview ————————————————————————————————————————
  // Always live — the preview recomputes the instant any input changes.
  const fixedPreview = React.useMemo(() => {
    const n = Math.max(0, Math.min(60, parseInt(count, 10) || 0));
    const days = Object.keys(dayTimes).map((d) => parseInt(d, 10)).sort((a, b) => a - b);
    if (!n || !days.length || !anchor) return [];
    const out = [];
    // Build the cursor from local Y/M/D components so getDay() reflects
    // the local calendar (NOT shifted by the UTC offset).
    const cur = localDateFromIso(anchor);
    let safety = 0;
    while (out.length < n && safety < 365 * 4) {
      const dow = cur.getDay();
      if (days.includes(dow)) {
        out.push({ iso: ymdLocal(cur), time: dayTimes[dow] || defaultTime });
      }
      cur.setDate(cur.getDate() + 1);
      safety += 1;
    }
    return out;
  }, [count, anchor, dayTimes]);

  // Nudge the total-session count by ±1, clamped to the same 1–60 range
  // enforced by the input's min/max so typed and stepped values agree.
  // Empty input (NaN) falls back to 1 (the minimum) so pressing + after
  // clearing advances to 2 rather than just re-establishing the floor.
  const stepCount = (delta) => {
    setCount((prev) => String(Math.max(1, Math.min(60, (parseInt(prev, 10) || 1) + delta))));
  };

  const toggleDay = (dow) => {
    setDayTimes((prev) => {
      const next = { ...prev };
      if (next[dow]) delete next[dow];else
      next[dow] = defaultTime;
      return next;
    });
  };
  const setDayTime = (dow, t) => {
    setDayTimes((prev) => ({ ...prev, [dow]: t }));
  };

  // — Custom-mode helpers ————————————————————————————————————————
  const addItem = () => {
    const last = items[items.length - 1];
    let suggested = todayIso;
    if (last && last.date) {
      const d = localDateFromIso(last.date);
      d.setDate(d.getDate() + 7);
      suggested = ymdLocal(d);
    }
    setItems((prev) => [...prev, { date: suggested, time: last?.time || '' }]);
  };
  const updateItem = (i, patch) => {
    setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  };
  const removeItem = (i) => {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleSave = () => {
    if (mode === 'fixed') {
      const entries = fixedPreview.map((p) => ({ iso: p.iso, time: p.time || null }));
      onSave(entries);
    } else {
      const entries = items.
      filter((it) => it.date).
      map((it) => ({ iso: it.date, time: it.time || null }));
      // Custom mode requires ≥1 entry so the card never goes empty.
      if (entries.length === 0) return;
      onSave(entries);
    }
  };

  const canSave = mode === 'fixed' ?
  fixedPreview.length > 0 :
  items.filter((it) => it.date).length > 0;

  return (
    <Sheet open={true} onClose={onClose} title={lang === 'zh' ? '編輯課堂日期' : 'Edit session schedule'}>
      <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Mode switcher */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4,
          padding: 4, borderRadius: 12, background: 'var(--bg-2)',
          border: '0.5px solid var(--border)'
        }}>
          {[
          { id: 'fixed', label: lang === 'zh' ? '固定日期' : 'Fixed schedule' },
          { id: 'custom', label: lang === 'zh' ? '不固定日期' : 'Custom dates' }].
          map((opt) =>
          <button key={opt.id}
          type="button"
          onClick={() => setMode(opt.id)}
          style={{
            appearance: 'none', cursor: 'pointer',
            padding: '10px 12px', borderRadius: 8,
            background: mode === opt.id ? 'var(--surface)' : 'transparent',
            border: 0, color: mode === opt.id ? 'var(--fg)' : 'var(--fg-3)',
            font: 'inherit', fontSize: 13, fontWeight: mode === opt.id ? 600 : 500,
            boxShadow: mode === opt.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            transition: 'background .15s, color .15s'
          }}>
              {opt.label}
            </button>
          )}
        </div>

        {/* Mode body */}
        {mode === 'fixed' ?
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '課堂總數' : 'Total sessions'}</div>
                <div className="stepper">
                  <button type="button" className="stepper-btn"
                aria-label={lang === 'zh' ? '減少' : 'Decrease'}
                onClick={() => stepCount(-1)}
                disabled={(parseInt(count, 10) || 0) <= 1}>−</button>
                  <input className="field-input stepper-input" type="number" min="1" max="60"
                value={count}
                onChange={(e) => setCount(e.target.value)} />
                  <button type="button" className="stepper-btn"
                aria-label={lang === 'zh' ? '增加' : 'Increase'}
                onClick={() => stepCount(1)}
                disabled={(parseInt(count, 10) || 0) >= 60}>+</button>
                </div>
              </div>
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '由此日期起' : 'Starting from'}</div>
                <DateField className="field-input" lang={lang} value={anchor}
              onChange={setAnchor} />
              </div>
            </div>

            <div className="field">
              <div className="field-label">{lang === 'zh' ? '每周幾' : 'Days of week'}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DOW_LABELS.map((label, dow) => {
                const on = !!dayTimes[dow];
                return (
                  <button key={dow}
                  type="button"
                  onClick={() => toggleDay(dow)}
                  style={{
                    appearance: 'none', cursor: 'pointer',
                    width: 38, height: 38, borderRadius: 999,
                    background: on ? 'var(--accent)' : 'var(--surface)',
                    border: on ? 0 : '0.5px solid var(--border)',
                    color: on ? '#fff' : 'var(--fg-2)',
                    font: 'inherit', fontSize: 13, fontWeight: on ? 600 : 500,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background .15s, color .15s'
                  }}>
                      {label}
                    </button>);

              })}
              </div>
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 6, letterSpacing: '0.02em' }}>
                {lang === 'zh' ? '可多選 — 例:每周一、三' : 'Multi-select — e.g. Mon, Wed'}
              </div>
            </div>

            {/* Per-day time inputs */}
            {Object.keys(dayTimes).length > 0 &&
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="eyebrow">{lang === 'zh' ? '每日時間' : 'Time per day'}</div>
                {Object.keys(dayTimes).
            map((d) => parseInt(d, 10)).
            sort((a, b) => a - b).
            map((dow) =>
            <div key={dow} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 8,
              background: 'var(--surface)',
              border: '0.5px solid var(--border)'
            }}>
                      <span style={{
                minWidth: 64, fontSize: 13, color: 'var(--fg)'
              }}>
                        {lang === 'zh' ? `每周${DOW_LABELS[dow]}` : DOW_LABELS[dow]}
                      </span>
                      <TimeField
                compact
                lang={lang}
                value={dayTimes[dow]}
                onChange={(v) => setDayTime(dow, v)}
                className="field-input"
                style={{ flex: 1, minWidth: 0 }} />
              
                    </div>
            )}
              </div>
          }

            {/* Preview list — live, recomputes the instant any input changes. */}
            {fixedPreview.length > 0 &&
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className="eyebrow" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                  <span>{lang === 'zh' ? '預覽' : 'Preview'}</span>
                  <span className="muted-2" style={{
                fontSize: 10, letterSpacing: '0.08em'
              }}>
                    {fixedPreview.length} {lang === 'zh' ? '節' : 'sessions'}
                  </span>
                </div>
                <div style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              maxHeight: 220, overflowY: 'auto',
              padding: '6px 8px', borderRadius: 10,
              background: 'var(--surface)',
              border: '0.5px solid var(--border)'
            }}>
                  {fixedPreview.map((p, i) => {
                const [yy, mm, dd] = p.iso.split('-').map((n) => parseInt(n, 10));
                const dow = new Date(yy, mm - 1, dd).getDay();
                return (
                  <div key={p.iso + i} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 4px', fontSize: 12.5,
                    borderBottom: i === fixedPreview.length - 1 ? 0 : '0.5px solid var(--border)'
                  }}>
                        <span className="mono muted-2" style={{ minWidth: 26, fontSize: 10, letterSpacing: '0.06em' }}>
                          #{i + 1}
                        </span>
                        <span style={{ flex: 1, color: 'var(--fg)' }}>
                          {`${dd}/${mm} (${DOW_LABELS[dow]}) · ${p.time}`}
                        </span>
                      </div>);

              })}
                </div>
              </div>
          }
          </div> :

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="muted-2" style={{ fontSize: 11.5, letterSpacing: '0.02em' }}>
              {lang === 'zh' ?
            '逐個加入課堂日期和時間。' :
            'Add each session date and time, one at a time.'}
            </div>
            {items.map((it, i) =>
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', borderRadius: 10,
            background: 'var(--surface)',
            border: '0.5px solid var(--border)'
          }}>
                <span className="mono muted-2" style={{
              fontSize: 10, letterSpacing: '0.08em',
              minWidth: 24, textAlign: 'center'
            }}>
                  #{i + 1}
                </span>
                <DateField
              compact
              lang={lang}
              value={it.date}
              onChange={(v) => updateItem(i, { date: v })}
              className="field-input"
              style={{ flex: 1.2, minWidth: 0 }} />
            
                <TimeField
              compact
              lang={lang}
              value={it.time}
              onChange={(v) => updateItem(i, { time: v })}
              className="field-input"
              style={{ flex: 1, minWidth: 0 }} />
            
                <button
              type="button"
              onClick={() => removeItem(i)}
              aria-label={lang === 'zh' ? '移除' : 'Remove'}
              disabled={items.length === 1}
              style={{
                width: 30, height: 30, borderRadius: 8,
                background: 'transparent',
                border: '0.5px solid var(--border)',
                color: items.length === 1 ? 'var(--fg-3)' : 'var(--danger)',
                cursor: items.length === 1 ? 'not-allowed' : 'pointer',
                opacity: items.length === 1 ? 0.5 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                  <Icon name="close" size={13} stroke={2} />
                </button>
              </div>
          )}
            <button
            type="button"
            onClick={addItem}
            style={{
              appearance: 'none', cursor: 'pointer',
              padding: '10px 12px', borderRadius: 10,
              background: 'transparent',
              border: '1px dashed var(--border-strong)',
              color: 'var(--accent)', font: 'inherit', fontSize: 13,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}>
              <Icon name="plus" size={13} stroke={2.2} />
              {lang === 'zh' ? '加入課堂' : 'Add session'}
            </button>
          </div>
        }

        {/* Action row */}
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          {hasOverride &&
          <button type="button" className="btn btn-ghost"
          onClick={onReset}
          style={{ flex: '0 0 auto' }}
          title={lang === 'zh' ? '還原為課程預設' : 'Reset to default'}>
              {lang === 'zh' ? '還原' : 'Reset'}
            </button>
          }
          <button type="button" className="btn btn-ghost"
          onClick={onClose}
          style={{ flex: '0 0 auto' }}>
            {lang === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button type="button" className="btn btn-primary btn-fill"
          disabled={!canSave}
          onClick={handleSave}>
            <Icon name="check" size={15} stroke={2.2} />
            {lang === 'zh' ? '儲存' : 'Save'}
          </button>
        </div>
      </div>
    </Sheet>);

}

// ─────────────────────────────────────────────────────────────────
// <InProgressSection> — list of enrolled courses + manually-logged
// in-progress records, merged into a single "正在參與的課程" list.
// Manual records become pseudo-course objects so the same card layout
// (progress, sessions, attendance) renders for both kinds — the only
// behavioural difference is the click handler (see ActionPickerDialog
// inside InProgressCourseCard).
// ─────────────────────────────────────────────────────────────────
function manualRecordToPseudoCourse(record, lang) {
  const startMatch = (record.notes || '').match(/START:(\d{4}-\d{2}-\d{2})/);
  const startDate = startMatch ? startMatch[1] : record.date;
  // Synthesise a session count — if start ≠ end we treat it as a
  // multi-day course, otherwise just one session. Capped so a wild
  // date range doesn't generate 200 pills.
  let sessions = 1;
  if (startDate && record.date && startDate !== record.date) {
    const [sy, sm, sd] = startDate.split('-').map((n) => parseInt(n, 10));
    const [ey, em, ed] = record.date.split('-').map((n) => parseInt(n, 10));
    const diff = Math.round((new Date(ey, em - 1, ed) - new Date(sy, sm - 1, sd)) / 86400000);
    sessions = Math.max(1, Math.min(12, Math.floor(diff / 7) + 1));
  }
  return {
    id: `manual-${record.id}`,
    title: record.title,
    provider: record.provider,
    providerShort: record.provider,
    topic: 'misc',
    topicLabel: lang === 'zh' ? { en: 'Manual entry', zh: '手動加入' } : { en: 'Manual entry', zh: '手動加入' },
    date: startDate,
    sessions,
    category: record.category,
    points: record.points,
    isManual: true,
    _record: record
  };
}

// ─────────────────────────────────────────────────────────────────
// <UpcomingCalendarSection> — collapsible month-grid overview of the
// user's upcoming sessions across all enrolled courses. Source of
// truth is the same resolveSessions + customSessions chain the
// InProgressCourseCard uses, so the calendar and the per-course
// tracker can never disagree about what counts as "next session".
//
// Default: collapsed. Header surfaces a one-line preview (count + next
// session) so the value is visible before expanding. Open state is
// persisted to localStorage so power users don't re-toggle each visit.
// ─────────────────────────────────────────────────────────────────
const CALENDAR_OPEN_KEY = 'cne_calendar_open_v1';

function UpcomingCalendarSection({ lang, enrolledIds, onOpenCourse }) {
  // ⚠️ HOOK-ORDERING CONSTRAINT — every hook (React.useState/useMemo here,
  // plus the useCustomSessions/useAttendance calls) MUST run unconditionally
  // before the `if (allSessions.length === 0) return null` gate below.
  // enrolledIds starts as Set() and hydrates async, so allSessions is empty on
  // the first render and non-empty later. An early-return placed above any hook
  // changes the hook count between those renders → React tears the component
  // down ("rendered more hooks than during the previous render"), which is
  // exactly what made this calendar disappear. Hooks now use `React.useX`
  // member calls, so react-hooks/rules-of-hooks WILL flag a regression here
  // (it recognises `React.useX`) — but keep this comment as intent. Do NOT add
  // an early return above the gate, and add new hooks above it too.
  const [open, setOpen] = React.useState(() => {
    try { return localStorage.getItem(CALENDAR_OPEN_KEY) === '1'; }
    catch (e) { return false; }
  });
  const [customAll] = useCustomSessions();
  const [attendance] = useAttendance();
  const todayIso = todayLocalIso();
  // Month being displayed in the grid; first-of-month so date arithmetic
  // stays clean across DST and 31-day months.
  const [cursor, setCursor] = React.useState(() => {
    const t = localDateFromIso(todayIso);
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [selectedIso, setSelectedIso] = React.useState(null);

  // Swipe-gesture scratch state. This is a ref (i.e. a hook), so it MUST stay
  // up here with the other hooks — above the `allSessions.length === 0`
  // early-return gate below (see the HOOK-ORDERING CONSTRAINT note above).
  // It tracks the active touch so a horizontal drag can flip the month.
  const swipeRef = React.useRef({ x: 0, y: 0, tracking: false, swiped: false });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(CALENDAR_OPEN_KEY, next ? '1' : '0'); }
      catch (e) { /* quota */ }
      return next;
    });
  };

  // All upcoming (future, not-yet-attended) sessions across enrolled
  // courses, flattened to one entry per (course, session).
  const allSessions = React.useMemo(() => {
    const courses = (typeof COURSES !== 'undefined' ? COURSES : [])
      .filter((c) => enrolledIds && enrolledIds.has(c.id));
    const out = [];
    for (const c of courses) {
      const list = resolveSessions(c, customAll);
      const my = attendance[c.id] || {};
      for (const s of list) {
        if (s.isPast) continue;
        if (my[s.idx] === 'attended') continue;
        out.push({ courseId: c.id, course: c, session: s });
      }
    }
    return out.sort((a, b) => {
      const ka = `${a.session.iso}T${a.session.time || '00:00'}`;
      const kb = `${b.session.iso}T${b.session.time || '00:00'}`;
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });
  }, [enrolledIds, customAll, attendance]);

  const byIso = React.useMemo(() => {
    const m = {};
    for (const e of allSessions) {
      (m[e.session.iso] = m[e.session.iso] || []).push(e);
    }
    return m;
  }, [allSessions]);

  const nextEntry = allSessions[0];
  const fmtNext = () => {
    const [y, m, d] = nextEntry.session.iso.split('-').map((n) => parseInt(n, 10));
    const t = nextEntry.session.time ? ` ${nextEntry.session.time}` : '';
    if (lang === 'zh') return `${m}月${d}日${t}`;
    return new Date(y, m - 1, d).toLocaleString('en', { month: 'short', day: 'numeric' }) + t;
  };

  // Build the 6-row × 7-col grid (Sun-first). Leading/trailing cells from
  // adjacent months are still rendered (faded) so the grid keeps a stable
  // rectangular shape.
  const grid = React.useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) {
      const d = new Date(year, month, 1 - (startDow - i));
      cells.push({ y: d.getFullYear(), m: d.getMonth(), d: d.getDate(), other: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ y: year, m: month, d, other: false });
    }
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1];
      const ld = new Date(last.y, last.m, last.d);
      ld.setDate(ld.getDate() + 1);
      cells.push({ y: ld.getFullYear(), m: ld.getMonth(), d: ld.getDate(), other: true });
    }
    return cells.map((c) => {
      const iso = `${c.y}-${String(c.m + 1).padStart(2, '0')}-${String(c.d).padStart(2, '0')}`;
      return { ...c, iso, isToday: iso === todayIso, isPast: iso < todayIso };
    });
  }, [cursor, todayIso]);

  // Nothing to show — hide the section (see HOOK-ORDERING CONSTRAINT at the
  // top of this function: this gate must stay below every hook).
  if (allSessions.length === 0) return null;

  const dowLabels = lang === 'zh'
    ? ['日', '一', '二', '三', '四', '五', '六']
    : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const monthLabel = lang === 'zh'
    ? `${cursor.getFullYear()}年${cursor.getMonth() + 1}月`
    : cursor.toLocaleString('en', { month: 'long', year: 'numeric' });

  const goPrev = () => setCursor((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1));
  const goNext = () => setCursor((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1));

  // ── Swipe left / right to change month (touch / iOS WKWebView) ──────────
  // Only horizontal-dominant gestures flip the month; vertical gestures are
  // left alone so the page can still scroll (the grid sets `touch-action:
  // pan-y`). A qualifying horizontal drag also sets `swiped`, so the day-cell
  // tap that can fire on touchend is swallowed instead of selecting a day.
  const SWIPE_THRESHOLD = 45; // px of horizontal travel required to fire
  const onCalTouchStart = (e) => {
    if (e.touches.length !== 1) { swipeRef.current.tracking = false; return; }
    const t = e.touches[0];
    swipeRef.current = { x: t.clientX, y: t.clientY, tracking: true, swiped: false };
  };
  const onCalTouchMove = (e) => {
    const s = swipeRef.current;
    if (!s.tracking || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - s.x;
    const dy = e.touches[0].clientY - s.y;
    if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) s.swiped = true;
  };
  const onCalTouchEnd = (e) => {
    const s = swipeRef.current;
    if (!s.tracking) return;
    s.tracking = false;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext(); else goPrev(); // swipe left → next, right → previous
    }
  };
  // True if the gesture that just ended was a swipe — callers skip the tap.
  const consumeSwipeTap = () => {
    if (swipeRef.current.swiped) { swipeRef.current.swiped = false; return true; }
    return false;
  };

  const goToday = () => {
    const t = localDateFromIso(todayIso);
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    setSelectedIso(todayIso);
  };

  const selectedSessions = selectedIso ? (byIso[selectedIso] || []) : [];

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={{
          appearance: 'none', cursor: 'pointer',
          background: 'transparent', border: 0,
          padding: '12px 14px',
          width: '100%', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 12,
          font: 'inherit', color: 'inherit'
        }}>
        <span style={{
          width: 32, height: 32, borderRadius: 10,
          background: 'var(--primary-soft)', color: 'var(--primary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          <Icon name="calendar" size={16} stroke={1.8} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="h-row" style={{ fontSize: 14, display: 'block' }}>
            {lang === 'zh' ? '課程行事曆' : 'Session calendar'}
          </span>
          <span className="mono muted-2" style={{
            fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase'
          }}>
            {lang === 'zh'
              ? `${allSessions.length} 節即將舉行 · 下一節 ${fmtNext()}`
              : `${allSessions.length} upcoming · next ${fmtNext()}`}
          </span>
        </span>
        <span style={{
          color: 'var(--fg-3)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform .18s ease'
        }}>
          <Icon name="chev-d" size={16} stroke={1.8} />
        </span>
      </button>

      {open &&
        <div style={{
          padding: '4px 14px 14px',
          borderTop: '0.5px solid var(--border)'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 0 8px'
          }}>
            <button type="button" onClick={goPrev}
              aria-label={lang === 'zh' ? '上個月' : 'Previous month'}
              style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'transparent', border: '0.5px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--fg-2)'
              }}>
              <Icon name="chev-l" size={14} stroke={1.8} />
            </button>
            <button type="button" onClick={goToday}
              className="mono"
              title={lang === 'zh' ? '回到今日' : 'Jump to today'}
              style={{
                appearance: 'none', background: 'transparent', border: 0,
                padding: '4px 10px', borderRadius: 8,
                color: 'var(--fg-1)', fontSize: 12.5,
                letterSpacing: '0.04em', cursor: 'pointer',
                fontWeight: 500
              }}>
              {monthLabel}
            </button>
            <button type="button" onClick={goNext}
              aria-label={lang === 'zh' ? '下個月' : 'Next month'}
              style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'transparent', border: '0.5px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--fg-2)'
              }}>
              <Icon name="chev-r" size={14} stroke={1.8} />
            </button>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 2, marginBottom: 4
          }}>
            {dowLabels.map((lbl, i) =>
              <div key={i} className="mono muted-2" style={{
                textAlign: 'center', fontSize: 10, letterSpacing: '0.06em',
                padding: '4px 0', textTransform: 'uppercase'
              }}>
                {lbl}
              </div>
            )}
          </div>

          <div
            onTouchStart={onCalTouchStart}
            onTouchMove={onCalTouchMove}
            onTouchEnd={onCalTouchEnd}
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 2, touchAction: 'pan-y'
            }}>
            {grid.map((cell, i) => {
              const dayEntries = byIso[cell.iso] || [];
              const hasSessions = dayEntries.length > 0;
              const isSelected = selectedIso === cell.iso;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { if (consumeSwipeTap()) return; setSelectedIso(cell.iso); }}
                  aria-label={
                    cell.iso + (hasSessions ? ` · ${dayEntries.length} ${lang === 'zh' ? '節' : 'sessions'}` : '')
                  }
                  style={{
                    appearance: 'none', cursor: hasSessions ? 'pointer' : 'default',
                    aspectRatio: '1 / 1',
                    border: cell.isToday ? '0.5px solid var(--accent)' : '0.5px solid transparent',
                    borderRadius: 8,
                    background: isSelected
                      ? 'var(--accent-soft)'
                      : hasSessions
                        ? 'color-mix(in oklab, var(--primary) 9%, var(--surface))'
                        : 'transparent',
                    color: cell.other ? 'var(--fg-3)' : (cell.isPast ? 'var(--fg-3)' : 'var(--fg-1)'),
                    opacity: cell.other ? 0.45 : 1,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 2, padding: 2,
                    font: 'inherit',
                    transition: 'background .12s ease'
                  }}>
                  <span style={{ fontSize: 12.5, lineHeight: 1, fontFamily: 'var(--font-serif)' }}>
                    {cell.d}
                  </span>
                  <span style={{
                    display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center',
                    minHeight: 4
                  }}>
                    {hasSessions && dayEntries.slice(0, 3).map((_, j) =>
                      <span key={j} style={{
                        width: 4, height: 4, borderRadius: '50%',
                        background: 'var(--primary)'
                      }} />
                    )}
                    {hasSessions && dayEntries.length > 3 &&
                      <span className="mono" style={{
                        fontSize: 8, color: 'var(--primary)', marginLeft: 1
                      }}>+{dayEntries.length - 3}</span>
                    }
                  </span>
                </button>
              );
            })}
          </div>

          {selectedIso && selectedSessions.length > 0 &&
            <div style={{
              marginTop: 12, paddingTop: 12,
              borderTop: '0.5px dashed var(--border)',
              display: 'flex', flexDirection: 'column', gap: 8
            }}>
              <div className="mono muted-2" style={{
                fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase'
              }}>
                {(() => {
                  const [y, m, d] = selectedIso.split('-').map((n) => parseInt(n, 10));
                  if (lang === 'zh') return `${y}年${m}月${d}日 · ${selectedSessions.length} 節`;
                  const label = new Date(y, m - 1, d).toLocaleString('en', {
                    month: 'long', day: 'numeric', year: 'numeric'
                  });
                  return `${label} · ${selectedSessions.length} ${selectedSessions.length === 1 ? 'session' : 'sessions'}`;
                })()}
              </div>
              {selectedSessions.map((e, i) =>
                <button
                  key={i}
                  type="button"
                  onClick={() => onOpenCourse && onOpenCourse(e.courseId)}
                  style={{
                    appearance: 'none', cursor: 'pointer',
                    background: 'var(--surface)', border: '0.5px solid var(--border)',
                    borderRadius: 10, padding: '10px 12px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    font: 'inherit', color: 'inherit', textAlign: 'left',
                    width: '100%'
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="h-row" style={{ fontSize: 13.5, lineHeight: 1.3 }}>
                      {L(e.course.title, lang)}
                    </div>
                    {(e.session.time || e.course.providerShort) &&
                      <div className="mono muted-2" style={{
                        fontSize: 10.5, letterSpacing: '0.06em', marginTop: 3, textTransform: 'uppercase'
                      }}>
                        {e.session.time || ''}
                        {e.session.time && e.course.providerShort ? ' · ' : ''}
                        {e.course.providerShort ? L(e.course.providerShort, lang) : ''}
                      </div>
                    }
                  </div>
                  <Icon name="chev-r" size={14} stroke={1.7} />
                </button>
              )}
            </div>
          }
        </div>
      }
    </div>
  );
}

function InProgressSection({ lang, enrolledIds, inProgressRecords = [], records = [], profile, onOpenCourse, onEditRecord, onLogCourse, onMarkComplete, onScanCourse }) {
  const [attendance, setAttendance] = useAttendance();
  const [customAll] = useCustomSessions();
  // Catalogue pools are module globals in data.js; this version stamp is the
  // dep that lets the memo below recompute once the background archived fetch
  // lands. Read at render time — see CATALOGUE_VERSION in data.js.
  const catalogueVersion = window.CATALOGUE_VERSION || 0;
  const items = React.useMemo(() => {
    // resolveEnrolledCourses is the SHARED resolver behind both 進行中 badges
    // (record viewbar + tab bar), so this list and those counts cannot diverge.
    // It also resolves archived enrolments, which `COURSES.filter()` dropped —
    // that gap is what produced a badge of 2 over an empty list.
    const catalog = resolveEnrolledCourses(enrolledIds).courses;
    const manual = (inProgressRecords || []).map((r) => manualRecordToPseudoCourse(r, lang));
    return [...catalog, ...manual];
  }, [enrolledIds, inProgressRecords, lang, catalogueVersion]);

  // Class reminders are delivered SERVER-SIDE now (notify-cron 'class' sweep),
  // so the Record screen no longer schedules anything on-device. The schedule
  // / attendance data above still drives the session UI below.

  if (items.length === 0) {
    return (
      <div className="vital-empty" style={{ marginTop: 8 }}>
        <div className="vital-empty-icon"><Icon name="calendar" size={22} stroke={1.7} /></div>
        <div className="vital-empty-title">
          {lang === 'zh' ? '未有正在進行的課程' : 'No active courses'}
        </div>
        <div className="vital-empty-sub">
          {lang === 'zh' ?
          '在「探索」頁面報名,或手動加入正在進行的課程,均會出現在這裡。' :
          'Enrol from Discover, or manually log an in-progress course — both will appear here.'}
        </div>
        {/* Third route, offered exactly where the need appears: a nurse looking
            at an empty 進行中 has usually just enrolled somewhere else and has
            the confirmation page or poster in hand. Deep-links to the AI step
            (same 'log:ai' target as Home's 立即試用). */}
        {onScanCourse &&
        <button type="button" className="btn btn-soft" style={{ marginTop: 12 }}
          onClick={onScanCourse}>
          <Icon name="sparkle" size={15} stroke={1.8} />
          {lang === 'zh' ? '掃描課程連結或海報' : 'Scan a course link or poster'}
        </button>
        }
      </div>);

  }

  return (
    // Stagger plays once on mount — `items` is memoised on enrolment/records
    // (not attendance), so ticking a session re-renders without remounting the
    // cards, and the cascade never replays on a tap. Surviving cards keep their
    // keys when one is completed, so only genuinely new cards animate in.
    <Stagger style={{ display: 'flex', flexDirection: 'column', gap: 12 }} delay={45} distance={8}>
      {items.map((c) =>
      <InProgressCourseCard
        key={c.id}
        course={c}
        lang={lang}
        attendance={attendance}
        onCycle={setAttendance}
        onSelect={() => {
          if (c.isManual) {
            // No catalog detail page exists — go straight to record edit.
            onEditRecord && onEditRecord(c._record);
          }
        }}
        onViewDetail={() => onOpenCourse && onOpenCourse(c.id)}
        onEditRecord={() => {
          // Catalog course: open the matching record if it exists, else
          // surface the log sheet prefilled with this course.
          const existing = (records || []).find((r) => r.courseId === c.id);
          if (existing) {
            onEditRecord && onEditRecord(existing);
          } else if (onLogCourse) {
            onLogCourse(c);
          } else {
            onEditRecord && onEditRecord(null);
          }
        }}
        onMarkComplete={onMarkComplete ? (sessions) => onMarkComplete(c, sessions) : null} />

      )}
    </Stagger>);

}

// ─────────────────────────────────────────────────────────────────
// <PendingVerificationSection> — enrolled courses whose last
// session has passed but have no record/certificate yet.
// ─────────────────────────────────────────────────────────────────
function PendingVerificationSection({ lang, enrolledIds, records, onUploadCert, onAdvance, onMarkComplete }) {
  const [attendance] = useAttendance();
  const [customAll] = useCustomSessions();
  const today = todayLocalIso();
  const catalogueVersion = window.CATALOGUE_VERSION || 0; // see InProgressSection

  // A "done" course = end date in the past AND not already in records.
  // We honour any user-edited schedule (customSessions) so a course the user
  // pushed into the future doesn't get prematurely surfaced as "pending".
  //
  // Enrolments resolve through resolveEnrolledCourses (active ∪ archived) — a
  // course archived right after it ran is precisely the one whose certificate
  // is still outstanding, so filtering COURSES alone hid the prompt.
  const pending = React.useMemo(() => {
    const loggedIds = new Set((records || []).map((r) => r.courseId).filter(Boolean));
    return resolveEnrolledCourses(enrolledIds).courses.filter((c) => {
      if (loggedIds.has(c.id)) return false;
      const ses = resolveSessions(c, customAll);
      const last = ses[ses.length - 1];
      return last && last.iso < today;
    });
  }, [enrolledIds, records, customAll, catalogueVersion]);

  // Hidden file input — single shared instance per section.
  const fileRef = React.useRef(null);
  const pendingForUploadRef = React.useRef(null);
  const [busyId, setBusyId] = React.useState(null);
  const [markingId, setMarkingId] = React.useState(null);

  const pickFile = (course) => {
    pendingForUploadRef.current = course;
    fileRef.current && fileRef.current.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const c = pendingForUploadRef.current;
    pendingForUploadRef.current = null;
    if (!file || !c) return;
    setBusyId(c.id);
    try {
      // Per-course upload — preview just simulates, real impl wires to Supabase.
      await onUploadCert?.(c.id, file);
      // Toast + advance: tell the parent we just completed this course.
      const t = document.createElement('div');
      t.className = 'vital-toast';
      t.textContent = lang === 'zh' ? '證書已上傳，已完成課程' : 'Certificate uploaded';
      document.body.appendChild(t);
      setTimeout(() => t.remove(), 1500);
      onAdvance?.(c);
    } finally {
      setBusyId(null);
    }
  };

  if (pending.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <input ref={fileRef} type="file" accept="image/*,application/pdf"
      style={{ display: 'none' }} onChange={onFile} />
      {/* Stagger plays once on mount; uploading a cert removes that card
          without remounting the survivors, so the cascade never replays. The
          file input stays outside so its ref is never routed through cloneElement. */}
      <Stagger style={{ display: 'flex', flexDirection: 'column', gap: 10 }} delay={45} distance={8}>
      {pending.map((c) => {
        const sessions = resolveSessions(c, customAll);
        const my = attendance[c.id] || {};
        const attended = sessions.filter((s) => my[s.idx] === 'attended').length;
        const missed = sessions.filter((s) => my[s.idx] === 'missed').length;
        const isBusy = busyId === c.id;
        const isMarking = markingId === c.id;
        return (
          <div key={c.id} className="card" style={{
            padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
            background: 'color-mix(in oklab, var(--accent) 4%, var(--surface))',
            borderColor: 'color-mix(in oklab, var(--accent) 25%, var(--border))'
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'var(--accent-soft)', color: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}>
                <Icon name="upload" size={17} stroke={1.7} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 3 }}>
                  {L(c.providerShort, lang)} · {lang === 'zh' ? '待上傳證書' : 'awaiting certificate'}
                </div>
                <div className="h-row" style={{ fontSize: 14 }}>{L(c.title, lang)}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {lang === 'zh' ?
                  `已上 ${attended} / ${sessions.length} 堂` + (missed > 0 ? ` · 缺席 ${missed} 堂` : '') :
                  `${attended} of ${sessions.length} sessions attended` + (missed > 0 ? ` · ${missed} missed` : '')}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div className="serif" style={{ fontSize: 22, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--accent)' }}>
                  +{c.points}
                </div>
                <div className="mono muted-2" style={{ fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase', marginTop: 3 }}>
                  {L(STR.pts, lang)}
                </div>
              </div>
            </div>
            <LoadingButton
              type="button"
              className="btn btn-primary btn-fill"
              isLoading={isBusy}
              disabled={isMarking}
              loadingLabel={lang === 'zh' ? '上傳中…' : 'Uploading…'}
              leftIcon={<Icon name="upload" size={15} stroke={2} />}
              onClick={() => pickFile(c)}
              style={{ padding: '10px 14px' }}>
              {lang === 'zh' ? '上傳證書並完成' : 'Upload certificate'}
            </LoadingButton>
            {/* Escape hatch — let the user mark the course complete without
                 a certificate (they can attach one later from the record).
                 Same flow as the in-progress card's 標記為已完成 button. */}
            {onMarkComplete &&
              <button
                type="button"
                disabled={isBusy || isMarking}
                onClick={async () => {
                  if (isBusy || isMarking) return;
                  setMarkingId(c.id);
                  try { await onMarkComplete(c, sessions); }
                  finally { setMarkingId(null); }
                }}
                style={{
                  appearance: 'none', cursor: (isBusy || isMarking) ? 'wait' : 'pointer',
                  background: 'transparent', border: 0, padding: '4px 6px',
                  color: 'var(--fg-2)', font: 'inherit', fontSize: 12.5,
                  textDecoration: 'underline', textUnderlineOffset: 3,
                  alignSelf: 'center', opacity: (isBusy || isMarking) ? 0.6 : 1
                }}>
                {isMarking ?
                  lang === 'zh' ? '處理中…' : 'Marking…' :
                  lang === 'zh' ? '標記為已完成 (稍後上傳證書)' : 'Mark complete without certificate'}
              </button>
            }
          </div>);

      })}
      </Stagger>
    </div>);

}

// ─────────────────────────────────────────────────────────────────
// Notes field schema
// ─────────────────────────────────────────────────────────────────
// The DB's `notes` column is a single text blob. We pack three things in:
//   • START:YYYY-MM-DD   — start date for date-range courses (so the Log /
//                          Edit sheets can show 開始日期 + 結束日期 without
//                          requiring a schema migration).
//   • IMAGES:[...]        — JSON array of up to 3 supabase storage paths
//                          for attached photos / images.
//   • freeform text       — everything else, the user-visible note.
// parseNoteBlob / buildNoteBlob keep this format reversible.
// ─────────────────────────────────────────────────────────────────
function parseNoteBlob(raw) {
  const out = { startDate: null, imagePaths: [], completed: false, excluded: false, pointsPending: false, nature: null, natureOther: '', text: '' };
  if (!raw) return out;
  const textLines = [];
  for (const line of String(raw).split('\n')) {
    const startM = line.match(/^START:(\d{4}-\d{2}-\d{2})$/);
    const imgM = line.match(/^IMAGES:(.+)$/);
    // NATURE:<id>[|free text] — the Annex A 活動性質 column. Persisted here so a
    // nurse's per-row choice survives; before this, nchkAnnexARows hardcoded
    // 'course' and every export silently reset it.
    const natM = line.match(/^NATURE:([a-z]+)(?:\|(.*))?$/);
    if (startM) {out.startDate = startM[1];continue;}
    if (natM) {out.nature = natM[1];out.natureOther = natM[2] || '';continue;}
    if (line === 'COMPLETED:1') {out.completed = true;continue;}
    // EXCLUDED:1 — user manually chose NOT to count this record toward their
    // CNE points (see isRecordExcluded in data.js). Points-only: the record
    // still shows in history and the completed-count, just not in any total.
    if (line === 'EXCLUDED:1') {out.excluded = true;continue;}
    // PTSTBD:1 — the user logged a course that hadn't happened yet and its CNE
    // points weren't published, so they left the field blank (see
    // isRecordPointsPending). Distinguishes "not published yet" from a
    // deliberate 0, which matters because a 0-point record silently becomes a
    // 0-point COMPLETED record the day its end date passes.
    if (line === 'PTSTBD:1') {out.pointsPending = true;continue;}
    if (imgM) {
      try {
        const arr = JSON.parse(imgM[1]);
        if (Array.isArray(arr)) out.imagePaths = arr.filter((p) => typeof p === 'string').slice(0, 3);
      } catch (e) {/* malformed marker — ignore */}
      continue;
    }
    textLines.push(line);
  }
  out.text = textLines.join('\n').replace(/^\n+|\n+$/g, '');
  return out;
}
function buildNoteBlob({ startDate, imagePaths, completed, excluded, pointsPending, nature, natureOther, text }) {
  const parts = [];
  if (startDate) parts.push(`START:${startDate}`);
  if (completed) parts.push('COMPLETED:1');
  if (excluded) parts.push('EXCLUDED:1');
  if (pointsPending) parts.push('PTSTBD:1');
  // Only persist a NON-default nature: 'course' is what nchkAnnexARows falls
  // back to, so writing it would add a marker to every record for no reason.
  if (nature && nature !== 'course') {
    parts.push(`NATURE:${nature}${nature === 'other' && natureOther ? `|${String(natureOther).replace(/\n/g, ' ')}` : ''}`);
  }
  if (imagePaths && imagePaths.length) parts.push(`IMAGES:${JSON.stringify(imagePaths.slice(0, 3))}`);
  if (text && text.trim()) parts.push(text);
  return parts.length ? parts.join('\n') : null;
}
// ─────────────────────────────────────────────────────────────────
// isRecordCompleted — true when the user has explicitly tapped
// "標記為已完成" on a record whose end date is still in the future.
// We can't rely solely on `record.date <= today` because the user
// can finish a course early (e.g. all sessions ticked off before
// the official last session). Persisted as `COMPLETED:1` in notes.
// ─────────────────────────────────────────────────────────────────
function isRecordCompleted(record) {
  if (!record || !record.notes) return false;
  return /(^|\n)COMPLETED:1(\n|$)/.test(record.notes);
}

// ─────────────────────────────────────────────────────────────────
// isRecordPointsPending — the user logged a course before it ran
// (typically an AI scan of a poster or course link for something
// they'd just enrolled in) and its CNE points weren't published, so
// the points field was left blank and saved as 0.
//
// ‼️ WHY THIS FLAG EXISTS. inProgressRecords is just `date > today`,
// so the day the end date passes, a 0-point record migrates into
// 已完成 and quietly contributes 0 to the cycle tally. For an app
// nurses rely on at licence renewal, a silently-undercounted record
// is the worst failure we can ship. The flag is what lets the
// in-progress card, the timeline and 標記為已完成 all say "學分待補"
// instead of letting it slide through. Cleared as soon as points are
// set to a non-zero value (see the record editor's save path).
// ─────────────────────────────────────────────────────────────────
function isRecordPointsPending(record) {
  if (!record || !record.notes) return false;
  if (Number(record.points) > 0) return false; // points arrived — flag is stale
  return /(^|\n)PTSTBD:1(\n|$)/.test(record.notes);
}

// ─────────────────────────────────────────────────────────────────
// compressImage — downscale + re-encode an image File before upload so
// phone photos (often 3–8 MB) land in storage as ~a few hundred KB.
// Non-image files (PDF certificates), animated GIFs, and anything that
// fails to decode are returned untouched so the upload still proceeds.
// The result is only used when it's actually smaller than the original.
// ─────────────────────────────────────────────────────────────────
async function compressImage(file, { maxEdge = 1600, quality = 0.8 } = {}) {
  try {
    if (!file || !file.type || !file.type.startsWith('image/')) return file;
    if (file.type === 'image/gif') return file; // don't flatten animation
    const bitmap = await createImageBitmap(file).catch(() => null);
    if (!bitmap) return file;

    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close?.(); return file; }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;

    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (e) {
    console.warn('compressImage failed — uploading original', e);
    return file;
  }
}

// ─────────────────────────────────────────────────────────────────
// <RecordDetailSheet> — edit a single record. Allows changing
// metadata + uploading/replacing the verification image.
// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// Qualification expiry
//
// Only a minority of CNE records expire, so the editor must not grow a date
// field that is blank-and-irrelevant for most entries. These two helpers keep
// the common path clean while making the credential path one tap away.
// ─────────────────────────────────────────────────────────────────────────

// Does this record look like a credential that lapses? Used ONLY to decide
// whether the expiry field starts open — never to write a value. Resuscitation
// cards are the overwhelming real-world case (AHA BLS/ACLS/PALS, valid 2 yrs).
const CREDENTIAL_TITLE_RE = /\b(bls|acls|pals|nrp|acls-ep|basic life support|advanced cardiac|advanced cardiovascular|paediatric advanced|pediatric advanced)\b|基礎生命支援|高級心臟?生命支援|兒科高級生命支援/i;
function recordLooksLikeCredential(record, form) {
  if (record?.expiresOn) return true;
  const title = [form?.title, form?.title_en, form?.title_zh, record?.title?.en, record?.title?.zh]
    .filter(Boolean).join(' ');
  return CREDENTIAL_TITLE_RE.test(title);
}

// Disclosure-wrapped date input. Closed → a single "add expiry" affordance.
// Open → a DateField plus a clear button, because removing an expiry the user
// (or the AI scan) added must be as easy as adding one.
function ExpiryField({ lang, value, minDate, suggest, onChange }) {
  const [open, setOpen] = React.useState(!!value || !!suggest);
  const label = lang === 'zh' ? '有效期至' : 'Valid until';
  if (!open) {
    return (
      <div className="field">
        <button type="button" className="filter-chip"
          onClick={() => setOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="clock" size={12} stroke={1.8} />
          {lang === 'zh' ? '加入有效期' : 'Add an expiry date'}
        </button>
      </div>
    );
  }
  return (
    <div className="field">
      <div className="field-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{label}</span>
        {value && (
          <button type="button" className="muted-2"
            onClick={() => { onChange(''); setOpen(false); }}
            style={{ appearance: 'none', background: 'transparent', border: 0, font: 'inherit', fontSize: 11, cursor: 'pointer' }}>
            {lang === 'zh' ? '清除' : 'Clear'}
          </button>
        )}
      </div>
      <DateField className="field-input" lang={lang} value={value}
        min={minDate}
        onChange={(v) => onChange(v)} />
      <div className="muted-2" style={{ fontSize: 11, marginTop: 4 }}>
        {lang === 'zh'
          ? '例如 BLS／ACLS 證書有效期。到期前 90、30、7 日會提醒你。'
          : 'e.g. a BLS/ACLS card. We’ll remind you 90, 30 and 7 days before.'}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// <DeleteRecordConfirm> — the confirmation step for deleting a CNE record.
//
// ‼️ This MUST stay a portaled overlay, never an inline block inside the sheet.
// It used to render in normal flow directly under the 刪除 button, which put it
// below the fold on any record with a session grid or a verification gallery:
// tapping 刪除 appeared to do nothing, and the user had to scroll to discover a
// destructive confirm they were never told existed. A portal to <body> escapes
// the Sheet's `overflow: hidden` + slide-up transform (the same reason
// AiScanDialog in screens.jsx portals), so the confirm is always centred in the
// viewport regardless of scroll position.
//
// Destructive-action rules encoded here:
//   • Focus lands on Cancel, not Delete — a stray Enter must not destroy data.
//   • Escape and a backdrop tap cancel; both are inert while the delete is in
//     flight so a half-finished write is never abandoned mid-request.
//   • Tab is trapped between the two buttons (the overlay is aria-modal, so
//     letting focus walk into the sheet behind it would lie to a screen reader).
//   • The Delete button self-disables while busy. DB.deleteRecord is a plain
//     Supabase call with no idempotency key, so a double tap used to fire two
//     deletes and two full reloadUserData() passes.
//   • DB.deleteRecord RESOLVES with `{ error }` rather than throwing. The old
//     inline block ignored the result and closed the sheet either way, so an
//     RLS rejection or an offline tap looked like a successful delete until the
//     record reappeared on the next refresh. Failures now stay put and say why.
// ─────────────────────────────────────────────────────────────────────────────
function DeleteRecordConfirm({ record, lang, verifyCount = 0, onCancel, onDeleted, onDelete }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  // ‼️ `busy` state alone CANNOT gate the delete. Three taps inside one frame
  // all read the pre-render value `false`, and React has not applied the
  // buttons' `disabled` attribute yet either — verified: it fired three
  // concurrent deletes. The ref flips synchronously on the first tap, so it is
  // the real guard; `busy` exists only to re-render. Every in-flight check
  // (confirm, Escape, backdrop) reads the REF, never the state.
  const busyRef = React.useRef(false);
  const cancelRef = React.useRef(null);
  const deleteRef = React.useRef(null);
  // Whoever opened the dialog gets focus back when it closes.
  const returnFocusRef = React.useRef(null);

  // ‼️ Focus SYNCHRONOUSLY here, not from a requestAnimationFrame callback.
  // useEffect already runs after the portal is committed, so the button exists;
  // rAF is throttled whenever the webview isn't compositing (backgrounded tab,
  // iOS low-power mode) and the initial focus then silently never lands, which
  // would leave Enter aimed at whatever was focused before the dialog opened.
  React.useEffect(() => {
    returnFocusRef.current = document.activeElement;
    cancelRef.current?.focus();
    return () => {
      const el = returnFocusRef.current;
      if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
    };
  }, []);

  // After a failed delete the buttons re-enable; pull focus back to the safe
  // one so the error is announced and Enter doesn't re-fire the delete. Keyed
  // on `err` so it runs after the re-render that un-disables the button —
  // focusing a still-disabled element is a no-op.
  React.useEffect(() => {
    if (err) cancelRef.current?.focus();
  }, [err]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); // the Sheet also listens for Escape — don't close both
        if (!busyRef.current) onCancel();
        return;
      }
      if (e.key !== 'Tab') return;
      // Two focusables only, so the trap is a plain two-way wrap.
      const first = cancelRef.current, last = deleteRef.current;
      if (!first || !last) return;
      const active = document.activeElement;
      if (active !== first && active !== last) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    // Capture phase: beat the Sheet's window-level Escape handler to the event.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const confirm = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setErr('');
    try {
      const res = await onDelete?.(record.id);
      // Supabase resolves with { error } instead of rejecting.
      if (res && res.error) throw res.error;
      onDeleted();
    } catch (e) {
      busyRef.current = false; // released only on failure — success unmounts us
      setBusy(false);
      // ‼️ NEVER render the raw error. Supabase surfaces Postgres text verbatim
      // ("new row violates row-level security policy for table records"), which
      // tells the user nothing and leaks table + policy names into a screenshot
      // a nurse may well send us. The operator detail goes to the console; the
      // user gets the one thing they can act on.
      try { console.warn('[record delete] failed:', (e && e.message) || String(e), e); } catch (_) {}
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      setErr(offline
        ? (lang === 'zh'
          ? '目前離線,無法刪除。請連上網絡後再試。'
          : "You're offline, so this couldn't be deleted. Reconnect and try again.")
        : (lang === 'zh'
          ? '刪除失敗,紀錄仍然保留。請稍後再試一次。'
          : "Couldn't delete — the record is still here. Please try again."));
    }
  };

  const title = L(record.title, lang) || (lang === 'zh' ? '這項紀錄' : 'this record');
  const points = Number(record.points) || 0;
  // Generated, not hardcoded — aria-labelledby silently points at the wrong
  // node if two dialogs ever share an id.
  const titleId = React.useId();
  const bodyId = React.useId();

  return window.ReactDOM.createPortal(
    <div
      className="rx-del-backdrop"
      onClick={() => { if (!busyRef.current) onCancel(); }}>
      <div
        className="rx-del-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onClick={(e) => e.stopPropagation()}>
        <div className="rx-del-icon" aria-hidden="true">
          <Icon name="trash" size={20} stroke={1.8} />
        </div>
        <div id={titleId} className="rx-del-heading">
          {lang === 'zh' ? '刪除這項紀錄？' : 'Delete this record?'}
        </div>
        <div id={bodyId} className="rx-del-body">
          <div className="rx-del-target">{title}</div>
          <div>
            {lang === 'zh'
              ? `此紀錄${points ? `的 ${points} 分` : ''}${verifyCount ? `及 ${verifyCount} 份已上傳的證明` : ''}將從你的 CNE 紀錄與 NCHK 匯出中移除,且無法復原。`
              : `Its ${points ? `${points} point${points === 1 ? '' : 's'}` : 'entry'}${verifyCount ? ` and ${verifyCount} uploaded proof${verifyCount === 1 ? '' : 's'}` : ''} will be removed from your CNE record and NCHK export. This can't be undone.`}
          </div>
        </div>
        {err && <div className="rx-del-error" role="alert">{err}</div>}
        <div className="rx-del-actions">
          <button ref={cancelRef} type="button" className="btn btn-ghost"
            disabled={busy}
            onClick={() => { if (!busyRef.current) onCancel(); }}>
            {lang === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button ref={deleteRef} type="button" className="btn rx-del-go"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={confirm}>
            {busy && <Spinner size={14} color="currentColor" />}
            <span>{busy
              ? (lang === 'zh' ? '刪除中…' : 'Deleting…')
              : (lang === 'zh' ? '刪除' : 'Delete')}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Which raw column is the single visible input actually editing?
//
// ‼️ PROVENANCE, NOT LOCALE. A Chinese UI showing a record that has no Chinese
// title is displaying the ENGLISH column; typing there is an edit to English.
// Only when a real Chinese value exists is the Chinese column the thing on
// screen. Deciding this by `lang` alone is the §56 defect; deciding it by
// "always _en" silently discards edits to genuine translations.
//
// Regression: scripts/test-record-edit-provenance.mjs — `npm run test:record-edit`.
function editedSourceField(base, form, lang) {
  const zh = form[`${base}_zh`];
  return (lang === 'zh' && zh !== null && zh !== undefined && zh !== '')
    ? `${base}_zh`
    : `${base}_en`;
}

function RecordDetailSheet({ record, lang, onClose, onSave, onUploadCert, onDelete }) {
  const parsedNotes = parseNoteBlob(record.notes);
  // ‼️ SEED FROM THE RAW SOURCE, NEVER FROM record.title / record.provider.
  // Those two carry a display fallback (zh ?? en, db.js), and seeding the form
  // from them turns "we showed her the English" into "she has a Chinese title"
  // the moment she saves — which is exactly how a corrected title became
  // invisible in the Chinese UI, permanently (RELEASE.md §56). `?? ''` only:
  // `|| ''` would erase a legitimately empty-string source the same way.
  const titleSrc = record.titleSource || { en: record.title?.en ?? null, zh: null };
  const providerSrc = record.providerSource || { en: record.provider?.en ?? null, zh: null };
  const [form, setForm] = React.useState({
    title: L(record.title, lang),
    title_en: titleSrc.en ?? '',
    title_zh: titleSrc.zh ?? '',
    provider: L(record.provider, lang),
    provider_en: providerSrc.en ?? '',
    provider_zh: providerSrc.zh ?? '',
    startDate: parsedNotes.startDate || record.date || '',
    endDate: record.date || '',
    points: String(record.points || 0),
    category: record.category || 'elective',
    // Optional qualification expiry (BLS/ACLS/PALS provider cards, specialty
    // certificates). '' = no expiry, which is the norm for ordinary CNE records.
    expiresOn: record.expiresOn || '',
    notesText: parsedNotes.text || ''
  });
  // Manual "count toward CNE points" state. Tracks the persisted EXCLUDED:1
  // flag; optimistic so the switch feels instant, reverted if the save fails.
  // Points-only: flipping this never moves the record between 進行中 / 已完成.
  const [excluded, setExcluded] = React.useState(() => parsedNotes.excluded);
  const [countBusy, setCountBusy] = React.useState(false);
  // Counting only applies once a record is completed (end date passed OR
  // marked done). For in-progress records the toggle + 不計分 chip are hidden —
  // but we NEVER strip EXCLUDED:1, so the user's choice survives a
  // complete→in-progress→complete round-trip (see the buildNoteBlob
  // preserve-sites in submit() and handleViewUpload()).
  const isCompleted = !!record.date && (record.date <= todayLocalIso() || isRecordCompleted(record));
  // Attached note-images: existing paths from supabase, plus newly-staged
  // File objects that get uploaded on Save.
  const [existingImages, setExistingImages] = React.useState(parsedNotes.imagePaths || []);
  const [newImages, setNewImages] = React.useState([]); // { file, previewUrl }
  const [existingUrls, setExistingUrls] = React.useState({}); // path → signed url
  const [imageUploadErr, setImageUploadErr] = React.useState('');
  const [certFile, setCertFile] = React.useState(null);
  const [certFileError, setCertFileError] = React.useState('');
  const [certPreview, setCertPreview] = React.useState(null);
  const [certUrl, setCertUrl] = React.useState(null); // signed url for the legacy single-cert
  const [busy, setBusy] = React.useState(false);
  const [delConfirm, setDelConfirm] = React.useState(false);
  // View vs edit. Default = view (course detail + verifications gallery).
  // Tapping "編輯" toggles into the existing form. New record sheets opened
  // from completed timeline land in view; in-progress edits still land in
  // edit (handled by the parent passing initialEditing=true when needed).
  const [editing, setEditing] = React.useState(false);
  // Zoom overlay — image src currently being viewed full-screen. Tap a
  // verification thumbnail to populate, tap backdrop to dismiss.
  const [zoomSrc, setZoomSrc] = React.useState(null);
  // While a view-mode upload is in flight we disable the +Upload tile
  // and show a small spinner so the user knows it's processing.
  const [viewUploading, setViewUploading] = React.useState(false);
  const [viewUploadErr, setViewUploadErr] = React.useState('');
  const fileRef = React.useRef(null);
  const noteImgRef = React.useRef(null);
  // Hidden picker used by the view-mode "+ Upload verification" CTA. Kept
  // separate from noteImgRef so re-opening the edit form doesn't conflict.
  const verifyFileRef = React.useRef(null);

  // ── Session attendance ──────────────────────────────────────────────────
  // Surface the same per-session tracker the 進行中 card uses, keyed by the
  // SAME bucket so ticks made there (and vice-versa) stay in sync. Catalog
  // records key on record.courseId; manual ones on `manual-<record.id>`.
  const [attendance, setAtt] = useAttendance();
  const [customAll, setCustom] = useCustomSessions();
  const [sessEditorOpen, setSessEditorOpen] = React.useState(false);
  const attKey = record.courseId || `manual-${record.id}`;
  // A course-shaped object for resolveSessions. Prefer the live catalog row
  // (richer raw schedule); fall back to a pseudo-course synthesised from the
  // record's own dates. Force .id = attKey so resolveSessions reads the same
  // override/tick bucket even if the catalog row was later removed.
  const catalogCourse = record.courseId && typeof COURSES !== 'undefined' ?
  COURSES.find((c) => c.id === record.courseId) : null;
  const courseForSessions = { ...(catalogCourse || manualRecordToPseudoCourse(record, lang)), id: attKey };
  const sessions = resolveSessions(courseForSessions, customAll);
  const my = attendance[attKey] || {};
  const attended = sessions.filter((s) => my[s.idx] === 'attended').length;
  const missed = sessions.filter((s) => my[s.idx] === 'missed').length;
  const total = sessions.length || 1;
  const pct = Math.round(attended / total * 100);
  const hasOverride = Array.isArray(customAll[attKey]);
  const saveSessions = (entries) => {
    setCustom(attKey, entries.map(serialiseSessionEntry).filter(Boolean));
  };
  const resetSessionsToDefault = () => setCustom(attKey, null);

  // Share-edited-schedule eligibility — catalog-linked records only, where the
  // user's effective schedule differs from what the course shows with no
  // override. Mirrors InProgressCourseCard; routes through course_reports
  // review, never a direct write. (See sameSchedule + the §13
  // sessions_list design.) shareOverrideList/shareCanonicalList feed the share
  // sheet's payload + report type below.
  const [shareOpen, setShareOpen] = React.useState(false);
  const shareOverrideList = Array.isArray(customAll[attKey]) ? customAll[attKey] : null;
  const shareCanonicalList = (catalogCourse && Array.isArray(catalogCourse.sessionsList))
    ? catalogCourse.sessionsList : null;
  const shareDefaultSessions = resolveSessions(courseForSessions, null);
  // True only when the saved override is a REAL change (drives the "自訂日期"
  // badge too, for manual + catalog records). The catalog-only eligibility
  // stays on canShareSchedule, since only catalog rows can take a correction.
  const scheduleChanged = hasOverride && !sameSchedule(sessions, shareDefaultSessions);
  const canShareSchedule = !!record.courseId && !!catalogCourse
    && scheduleChanged;

  // Whether this record is "in progress" — drives certificate visibility.
  const TODAY_ISO = todayLocalIso();
  const isInProgress = !!form.endDate && form.endDate > TODAY_ISO;

  // Resolve signed URLs for existing note-images so we can render them.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = { ...existingUrls };
      for (const p of existingImages) {
        if (next[p]) continue;
        const url = await (window.DB?.getNoteImageUrl?.(p) || Promise.resolve(null));
        if (cancelled) return;
        if (url) next[p] = url;
      }
      if (!cancelled) setExistingUrls(next);
    })();
    return () => {cancelled = true;};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingImages]);

  // Resolve a signed URL for the legacy single-certificate so the view-mode
  // gallery can render it as one of the verification tiles. Older records
  // (uploaded before the multi-verification refactor) only have this.
  React.useEffect(() => {
    let cancelled = false;
    const path = record.certificateUrl;
    if (!path) {setCertUrl(null);return;}
    (async () => {
      const url = await (window.DB?.getCertificateUrl?.(path) || Promise.resolve(null));
      if (cancelled) return;
      setCertUrl(url || null);
    })();
    return () => {cancelled = true;};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.certificateUrl]);

  // Build a preview URL when a file is chosen
  React.useEffect(() => {
    if (!certFile) {setCertPreview(null);return;}
    const url = URL.createObjectURL(certFile);
    setCertPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [certFile]);

  // Revoke object URLs for new note-images when the component unmounts.
  React.useEffect(() => {
    return () => {
      newImages.forEach((img) => {try {URL.revokeObjectURL(img.previewUrl);} catch (e) {}});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalImages = existingImages.length + newImages.length;
  const canAddMoreImages = totalImages < 3;

  const onPickNoteImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (!files.length) return;
    setImageUploadErr('');
    const remaining = 3 - totalImages;
    const staged = [];
    for (const f of files.slice(0, remaining)) {
      if (!f.type.startsWith('image/')) {
        setImageUploadErr(lang === 'zh' ? '只接受圖片檔案' : 'Only image files are allowed');
        continue;
      }
      // Compress first so a large phone photo still fits under the cap.
      const compressed = await compressImage(f);
      if (compressed.size > 5 * 1024 * 1024) {
        setImageUploadErr(lang === 'zh' ? '單張圖片不能超過 5 MB' : 'Each image must be 5 MB or smaller');
        continue;
      }
      staged.push({ file: compressed, previewUrl: URL.createObjectURL(compressed) });
    }
    if (staged.length) setNewImages((prev) => [...prev, ...staged]);
  };

  const removeExistingImage = (path) => {
    setExistingImages((prev) => prev.filter((p) => p !== path));
    // best-effort delete from storage — we don't block on this
    window.DB?.deleteNoteImage?.(path).catch(() => {});
  };
  const removeNewImage = (i) => {
    setNewImages((prev) => {
      const next = [...prev];
      const [removed] = next.splice(i, 1);
      if (removed) {try {URL.revokeObjectURL(removed.previewUrl);} catch (e) {}}
      return next;
    });
  };

  const hasCert = record.certificate || record.certificateUrl || certFile;
  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  // ── Unified verifications model (view mode) ─────────────────────────────
  // Combines the legacy single certificate column with the multi-image note
  // attachments into one capped-at-3 gallery.
  //   • Slot 0..N-1: existing note images (paths in record.notes IMAGES:[...])
  //   • Optional extra slot: the legacy certificate_url if present
  // View mode is read-only for deletion — tiles can be zoomed and new ones
  // added, but removing an image is only offered in edit mode (編輯課程) so a
  // stray tap can't destroy a verification. New uploads always go to the
  // note-images bucket (uploadNoteImage), keeping the cert column read-only.
  const verifyTiles = [];
  for (const p of existingImages) {
    verifyTiles.push({ kind: 'note', path: p, url: existingUrls[p] || null });
  }
  if (record.certificateUrl) {
    verifyTiles.push({ kind: 'cert', path: record.certificateUrl, url: certUrl });
  }
  const verifyCount = verifyTiles.length;
  const canAddVerify = verifyCount < 3 && !viewUploading;

  // Upload from view mode: stage one file → push to storage → patch the
  // record's notes blob with the new path. We bypass the full submit() path
  // (which also wants form fields validated) so the user can drop in a cert
  // without entering edit mode.
  const handleViewUpload = async (file) => {
    if (!file) return;
    setViewUploadErr('');
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setViewUploadErr(lang === 'zh' ? '只接受圖片或 PDF 檔案' : 'Image or PDF only');
      return;
    }
    // Shrink images before the size check + upload; PDFs pass through as-is.
    const toUpload = file.type.startsWith('image/') ? await compressImage(file) : file;
    if (toUpload.size > 8 * 1024 * 1024) {
      setViewUploadErr(lang === 'zh' ? '檔案不能超過 8 MB' : 'File must be 8 MB or smaller');
      return;
    }
    setViewUploading(true);
    try {
      const sess = await window.AuthAPI?.getSession?.().catch(() => null);
      const userId = sess?.user?.id || null;
      if (!userId) {
        setViewUploadErr(lang === 'zh' ? '請先登入' : 'Please sign in first');
        return;
      }
      const res = await window.DB?.uploadNoteImage?.(userId, record.id, toUpload);
      if (!res || res.error || !res.path) {
        setViewUploadErr(lang === 'zh' ? '上傳失敗，請再試' : 'Upload failed — please try again');
        return;
      }
      const nextPaths = [...existingImages, res.path].slice(0, 3);
      // Persist via onSave with only the notes patch. Title/provider/date
      // stay untouched.
      await onSave?.(record.id, {
        notes: buildNoteBlob({
          startDate: parsedNotes.startDate || form.startDate || null,
          imagePaths: nextPaths,
          completed: parsedNotes.completed || isRecordCompleted(record),
          // Preserve the user's don't-count choice across a cert/image upload.
          excluded: parsedNotes.excluded,
          // Same for "points not published yet" — uploading a photo must not
          // silently turn a pending-points record into a real 0-point one.
          pointsPending: parsedNotes.pointsPending,
          // Preserve the Annex A 活動性質 choice too — same reasoning as
          // COMPLETED:1 and EXCLUDED:1 above.
          nature: parsedNotes.nature,
          natureOther: parsedNotes.natureOther,
          text: parsedNotes.text || form.notesText || ''
        })
      });
      // Optimistically reflect locally so the gallery updates without
      // waiting for the parent to push a new `record` prop.
      setExistingImages(nextPaths);
    } finally {
      setViewUploading(false);
    }
  };

  // Pretty-format an ISO date according to the lang.
  const fmtDateLong = (iso) => {
    if (!iso) return '—';
    try {
      const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
      const dt = new Date(y, m - 1, d);
      return lang === 'zh' ?
      `${y}年${m}月${d}日` :
      dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {return iso;}
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Resolve user id once for all note-image uploads.
      const sess = await window.AuthAPI?.getSession?.().catch(() => null);
      const userId = sess?.user?.id || null;

      // Upload any newly-staged note images first so we have their paths.
      const uploadedPaths = [];
      for (const img of newImages) {
        if (!userId) break;
        const res = await window.DB?.uploadNoteImage?.(userId, record.id, img.file);
        if (res && res.path) uploadedPaths.push(res.path);else
        if (res && res.error) console.warn('uploadNoteImage failed', res.error);
      }
      const finalImagePaths = [...existingImages, ...uploadedPaths].slice(0, 3);

      const patch = {
        // ‼️ NO FALLBACK ON WRITE. This used to read
        //   zh: form.title_zh || form.title_en || form.title
        // which reconstructed the display fallback and stored it as a real
        // Chinese title. Once stored, `title_zh || title_en` stops firing, the
        // Chinese UI is pinned to the stale text, and no further edit can undo
        // it — a nurse corrected the same title twice and watched nothing
        // change (RELEASE.md §56). An empty source stays NULL: absence is a
        // fact about the record, not a gap to be filled at save time.
        title: { en: form.title_en || form.title, zh: form.title_zh || null },
        provider: { en: form.provider_en || form.provider, zh: form.provider_zh || null },
        date: form.endDate,
        year: Number((form.endDate || '').slice(0, 4)) || record.year,
        points: Number(form.points) || 0,
        category: form.category,
        // '' → null in db.js updateRecord: clearing the field is a real edit
        // ("this doesn't expire after all"), not an omission.
        expiresOn: form.expiresOn || null,
        notes: buildNoteBlob({
          startDate: form.startDate || null,
          imagePaths: finalImagePaths,
          // Preserve the COMPLETED:1 marker — without this an edit would
          // strip the early-finish flag and silently push the record back
          // into 進行中 if its end date is still in the future.
          completed: parsedNotes.completed || isRecordCompleted(record),
          // Likewise preserve EXCLUDED:1 so editing metadata never silently
          // re-includes a record the user chose not to count. The toggle in
          // view mode is the only place this flag is intentionally changed.
          excluded: parsedNotes.excluded,
          // PTSTBD:1 is the one marker this save may legitimately CLEAR: the
          // points field is right here, so entering a real value is exactly
          // the "points have been published" event the flag was waiting for.
          pointsPending: parsedNotes.pointsPending && !(Number(form.points) > 0),
          // Preserve the Annex A 活動性質 choice too — same reasoning as above.
          nature: parsedNotes.nature,
          natureOther: parsedNotes.natureOther,
          text: form.notesText
        })
      };
      if (certFile && !isInProgress) {
        await onUploadCert?.(record.id, certFile);
        patch.certificate = true;
      }
      await onSave?.(record.id, patch);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  // Flip the "count toward CNE points" switch. Notes-only patch (mirrors the
  // view-mode upload path) preserving every other parsed field; optimistic with
  // revert on failure. `next` is the new EXCLUDED value (switch ON = counted).
  const toggleCounted = async () => {
    if (countBusy) return;
    const next = !excluded;
    setExcluded(next);
    setCountBusy(true);
    try {
      await onSave?.(record.id, {
        notes: buildNoteBlob({
          startDate: parsedNotes.startDate || form.startDate || null,
          imagePaths: existingImages,
          completed: parsedNotes.completed || isRecordCompleted(record),
          excluded: next,
          pointsPending: parsedNotes.pointsPending,
          nature: parsedNotes.nature,
          natureOther: parsedNotes.natureOther,
          text: parsedNotes.text || form.notesText || ''
        })
      });
    } catch (e) {
      setExcluded(!next); // revert — save didn't land
    } finally {
      setCountBusy(false);
    }
  };

  return (
    <Sheet open={true} onClose={onClose} title={editing ? lang === 'zh' ? '編輯紀錄' : 'Edit record' : lang === 'zh' ? '紀錄詳情' : 'Record detail'}>
      {/* Hidden file input shared by the view-mode "+ Upload verification"
           CTA. Kept outside the conditional so refs are stable on toggle. */}
      <input
        ref={verifyFileRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) handleViewUpload(f);
        }} />
      

      {!editing ? (
      /* ── VIEW MODE ────────────────────────────────────────────────── */
      <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Course header — title, provider, status chip */}
          <div>
            <div className="mono muted-2" style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 6 }}>
              {L(record.provider, lang)}
            </div>
            <div className="serif" style={{ fontSize: 22, lineHeight: 1.15, letterSpacing: '-0.01em', textWrap: 'pretty' }}>
              {L(record.title, lang)}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, alignItems: 'center' }}>
              <span className="chip">
                <CatDot cat={record.category || 'elective'} size={6} />
                {L(STR[record.category] || STR.elective, lang)}
              </span>
              <span className="chip" style={{
                textDecoration: (isCompleted && excluded) ? 'line-through' : undefined,
                opacity: (isCompleted && excluded) ? 0.5 : undefined
              }}>
                <span className="serif" style={{ fontSize: 13, letterSpacing: '-0.01em' }}>+{record.points}</span>
                <span className="muted-2" style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', marginLeft: 3 }}>
                  {L(STR.pts, lang)}
                </span>
              </span>
              {(() => {
              const inProg = !!record.date && record.date > todayLocalIso() && !isRecordCompleted(record);
              return (
                <span className="chip" style={{
                  color: inProg ? 'var(--accent)' : 'var(--success)'
                }}>
                    <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: inProg ? 'var(--accent)' : 'var(--success)',
                    display: 'inline-block'
                  }} />
                    {inProg ?
                  lang === 'zh' ? '進行中' : 'In progress' :
                  lang === 'zh' ? '已完成' : 'Completed'}
                  </span>);

            })()}
              {/* Manual don't-count marker — only meaningful once completed. */}
              {isCompleted && excluded && (
                <span className="chip chip-uncounted">
                  {lang === 'zh' ? '不計分' : 'Not counted'}
                </span>
              )}
            </div>
          </div>

          {/* Date range card */}
          <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
          border: '0.5px solid var(--border)', borderRadius: 12,
          overflow: 'hidden', background: 'var(--surface)'
        }}>
            <div style={{ padding: '12px 14px' }}>
              <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                {lang === 'zh' ? '開始日期' : 'Start date'}
              </div>
              <div style={{ fontSize: 14, marginTop: 4 }}>
                {fmtDateLong(parsedNotes.startDate || record.date)}
              </div>
            </div>
            <div style={{ padding: '12px 14px', borderLeft: '0.5px solid var(--border)' }}>
              <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                {lang === 'zh' ? '結束日期' : 'End date'}
              </div>
              <div style={{ fontSize: 14, marginTop: 4 }}>
                {fmtDateLong(record.date)}
              </div>
            </div>
          </div>

          {/* Count-toward-points switch — shown only for completed records
               (counting is meaningless while in progress). Flipping it
               writes/clears EXCLUDED:1 via toggleCounted. The whole row is the
               tap target so it clears the 44px minimum. */}
          {isCompleted && (
            <div className="count-switch-row">
              <span className="count-switch-text">
                <span className="count-switch-label">
                  {lang === 'zh' ? '計入 CNE 分數' : 'Count toward CNE points'}
                </span>
                <span className="count-switch-help">
                  {lang === 'zh'
                    ? '關閉後此紀錄仍會保留在你的紀錄中,但不會計入分數或匯出。'
                    : 'When off, this record stays in your history but isn’t added to your CNE points or export.'}
                </span>
              </span>
              <Toggle on={!excluded} onChange={toggleCounted} busy={countBusy}
                ariaLabel={lang === 'zh' ? '計入 CNE 分數' : 'Count toward CNE points'} />
            </div>
          )}

          {/* Attendance summary (read-only) — session count + per-session
               status. Editing happens in 編輯課程 mode (tappable pills there). */}
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <div className="eyebrow">
                {lang === 'zh' ? '出席情況' : 'Attendance'}
              </div>
              <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.08em' }}>
                {attended}/{total} · {total} {lang === 'zh' ? '節' : 'sessions'}
              </div>
            </div>
            <div className="progress-bar" style={{ height: 4, marginBottom: 12 }}>
              <div style={{ width: pct + '%', background: 'var(--success)' }} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {sessions.map((s) =>
            <SessionPill key={s.idx} session={s} status={my[s.idx]} onCycle={() => {}} />
            )}
            </div>
            <div style={{
            display: 'flex', gap: 12, alignItems: 'center',
            fontSize: 11.5, color: 'var(--fg-2)', marginTop: 10
          }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)' }} />
                {attended} {lang === 'zh' ? '已上' : 'attended'}
              </span>
              {missed > 0 &&
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger)' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)' }} />
                  {missed} {lang === 'zh' ? '冇上' : 'missed'}
                </span>
            }
            </div>
          </div>

          {/* Notes display (read-only) */}
          {parsedNotes.text &&
        <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>
                {lang === 'zh' ? '備註' : 'Notes'}
              </div>
              <div style={{
            padding: '12px 14px', borderRadius: 10,
            background: 'var(--surface)', border: '0.5px solid var(--border)',
            fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', textWrap: 'pretty'
          }}>
                {parsedNotes.text}
              </div>
            </div>
        }

          {/* Verifications gallery — up to 3 thumbs, tap to zoom */}
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <div className="eyebrow">
                {lang === 'zh' ? '上傳的證明' : 'Verifications'}
              </div>
              <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.08em' }}>
                {verifyCount}/3
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {verifyTiles.map((tile, idx) =>
            <div key={`${tile.kind}-${tile.path}`} style={{
              position: 'relative',
              aspectRatio: '1 / 1',
              borderRadius: 12, overflow: 'hidden',
              background: 'var(--surface-2)',
              border: '0.5px solid var(--border)'
            }}>
                  {tile.url ?
              /\.pdf$/i.test(tile.path || '') ?
              <button type="button"
              onClick={() => setZoomSrc({ src: tile.url, kind: 'pdf', label: tile.kind })}
              style={{
                width: '100%', height: '100%', cursor: 'zoom-in',
                background: 'color-mix(in oklab, var(--accent) 6%, var(--surface))',
                color: 'var(--accent)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 6, border: 0, padding: 0
              }}>
                        <Icon name="cert" size={26} stroke={1.6} />
                        <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.08em' }}>PDF</span>
                      </button> :

              <button type="button"
              onClick={() => setZoomSrc({ src: tile.url, kind: 'image', label: tile.kind })}
              aria-label={lang === 'zh' ? '查看證明' : 'View verification'}
              style={{
                width: '100%', height: '100%', padding: 0, border: 0,
                background: 'transparent', cursor: 'zoom-in'
              }}>
                        <img src={tile.url} alt="" style={{
                  width: '100%', height: '100%', objectFit: 'cover', display: 'block'
                }} />
                      </button> :


              <div style={{
                width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--fg-3)'
              }}>
                      <Icon name="upload" size={20} stroke={1.5} />
                    </div>
              }
                  {tile.kind === 'cert' &&
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)',
                color: '#fff', fontSize: 9.5, padding: '3px 6px',
                letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center'
              }}>
                      {lang === 'zh' ? '主要證書' : 'Certificate'}
                    </div>
              }
                </div>
            )}
              {canAddVerify &&
            <button type="button"
            onClick={() => verifyFileRef.current?.click()}
            disabled={viewUploading}
            style={{
              aspectRatio: '1 / 1',
              borderRadius: 12,
              background: 'transparent',
              border: '1px dashed var(--border-strong)',
              color: 'var(--fg-3)',
              cursor: viewUploading ? 'wait' : 'pointer',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 6
            }}>
                  {viewUploading ?
              <span className="cert-upload-spinner" aria-hidden="true" /> :

              <Icon name="plus" size={18} stroke={2} />
              }
                  <span style={{ fontSize: 10.5, letterSpacing: '0.04em', textAlign: 'center', padding: '0 4px' }}>
                    {viewUploading ?
                lang === 'zh' ? '上傳中…' : 'Uploading…' :
                lang === 'zh' ? '加入證明' : 'Add'}
                  </span>
                </button>
            }
            </div>
            {verifyCount === 0 && !canAddVerify &&
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                {lang === 'zh' ? '未有證明上傳' : 'No verifications uploaded yet'}
              </div>
          }
            {viewUploadErr &&
          <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 8 }}>
                {viewUploadErr}
              </div>
          }
            <div className="muted-2" style={{ fontSize: 10.5, marginTop: 8, letterSpacing: '0.02em' }}>
              {lang === 'zh' ?
            '最多 3 份證明 · 圖片或 PDF · 單檔最大 8 MB · 點擊圖片可放大查看' :
            'Up to 3 verifications · image or PDF · 8 MB each · tap to zoom'}
            </div>
          </div>

          {/* Action row — Edit / Delete */}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="button" className="btn btn-ghost"
          style={{ flex: '0 0 auto', color: 'var(--danger)' }}
          onClick={() => setDelConfirm(true)}>
              <Icon name="close" size={14} stroke={2} />
              {lang === 'zh' ? '刪除' : 'Delete'}
            </button>
            <button type="button" className="btn btn-primary btn-fill"
          onClick={() => setEditing(true)}>
              <Icon name="edit" size={14} stroke={2} />
              {lang === 'zh' ? '編輯課程' : 'Edit course'}
            </button>
          </div>
          {/* The confirm itself is portaled from the sheet root — see
              <DeleteRecordConfirm>. Do NOT re-add an inline block here. */}
        </div>) : (

      /* ── EDIT MODE (existing form) ──────────────────────────────────── */
      <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Back-to-view link */}
        <button type="button" className="filter-chip"
        onClick={() => setEditing(false)}
        style={{ alignSelf: 'flex-start' }}>
          <Icon name="chev-l" size={13} stroke={1.7} />
          {lang === 'zh' ? '返回詳情' : 'Back to detail'}
        </button>

        {/* Verification preview block — hidden for in-progress records. */}
        {!isInProgress &&
        <div className="rx-cert-block">
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            {lang === 'zh' ? '證書 / 驗證' : 'Certificate / verification'}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 8 * 1024 * 1024) {
                setCertFileError(lang === 'zh'
                  ? '檔案過大。請選擇 8 MB 以內的 PDF、JPG 或 PNG。'
                  : 'File too large. Choose a PDF, JPG or PNG up to 8 MB.');
                e.target.value = '';
                return;
              }
              setCertFileError('');
              setCertFile(f);
            }} />
          {certFileError && (
            <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, marginBottom: 8 }}>
              {certFileError}
            </div>
          )}
          
          {certPreview ?
          <div className="rx-cert-preview">
              {certFile?.type?.startsWith('image/') ?
            <img src={certPreview} alt="cert preview" /> :

            <div className="rx-cert-pdf">
                  <Icon name="cert" size={32} stroke={1.5} />
                  <div style={{ fontSize: 12, marginTop: 6 }}>{certFile.name}</div>
                  <div className="muted-2" style={{ fontSize: 10.5, marginTop: 2 }}>{fmtSize(certFile.size)}</div>
                </div>
            }
              <div className="rx-cert-preview-actions">
                <button type="button" className="btn btn-ghost"
              onClick={() => fileRef.current?.click()}>
                  <Icon name="upload" size={13} stroke={1.8} />
                  {lang === 'zh' ? '更換檔案' : 'Replace'}
                </button>
                <button type="button" className="btn btn-ghost"
              onClick={() => { setCertFile(null); setCertFileError(''); }}>
                  <Icon name="close" size={13} stroke={2} />
                  {lang === 'zh' ? '移除' : 'Remove'}
                </button>
              </div>
            </div> :
          hasCert ?
          <div className="rx-cert-existing">
              <div className="rx-cert-existing-icon">
                <Icon name="check-circ" size={20} stroke={1.7} color="var(--success)" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="h-row" style={{ fontSize: 13.5 }}>
                  {lang === 'zh' ? '證書已上傳' : 'Certificate on file'}
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                  {record.certificateUrl ?
                record.certificateUrl.split('/').pop() :
                lang === 'zh' ? '已驗證' : 'verified'}
                </div>
              </div>
              <button type="button" className="btn btn-ghost"
            onClick={() => fileRef.current?.click()}
            style={{ padding: '7px 12px' }}>
                {lang === 'zh' ? '更換' : 'Replace'}
              </button>
            </div> :

          <button type="button" className="rx-cert-upload-cta"
          onClick={() => fileRef.current?.click()}>
              <div className="rx-cert-upload-icon">
                <Icon name="upload" size={22} stroke={1.7} />
              </div>
              <div>
                <div className="h-row" style={{ fontSize: 14 }}>
                  {lang === 'zh' ? '上傳證書' : 'Upload certificate'}
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                  {lang === 'zh' ? 'PDF / JPG / PNG · 最大 8 MB' : 'PDF / JPG / PNG · 8 MB max'}
                </div>
              </div>
            </button>
          }
        </div>
        }

        {/* In-progress hint — replaces the cert block so the user knows why
             it's hidden and what would unlock it. */}
        {isInProgress &&
        <div style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          padding: '12px 14px', borderRadius: 10,
          background: 'color-mix(in oklab, var(--accent) 8%, var(--surface))',
          border: '0.5px solid color-mix(in oklab, var(--accent) 30%, transparent)'
        }}>
            <Icon name="calendar" size={16} stroke={1.8} color="var(--accent)" />
            <div style={{ fontSize: 12.5, color: 'var(--fg-2)', lineHeight: 1.5 }}>
              {lang === 'zh' ?
            '此紀錄為「進行中」,證書欄會在結束日期當天或之後出現。' :
            'This record is in progress — the certificate field will appear once the end date has passed.'}
            </div>
          </div>
        }

        {/* Edit fields.
            ‼️ ONE BOX, TWO POSSIBLE SOURCES. The value on screen is L(record.x,
            lang): in a Chinese UI it is the Chinese column when one exists, and
            the English column when it does not. Editing it must write back to
            WHICHEVER COLUMN SUPPLIED IT — writing `_en` unconditionally loses a
            real Chinese title's edit, and writing `_zh` because the locale is zh
            invents a Chinese value out of an English fallback. Both mistakes
            have shipped; the second one is §56. `editedSourceField` answers
            "where did this come from", never "what locale am I in". */}
        <div className="field">
          <div className="field-label">{L(STR.courseTitle, lang)}</div>
          <input className="field-input" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value,
            [editedSourceField('title', form, lang)]: e.target.value })} />
        </div>
        <div className="field">
          <div className="field-label">{L(STR.provider, lang)}</div>
          <input className="field-input" value={form.provider}
          onChange={(e) => setForm({ ...form, provider: e.target.value,
            [editedSourceField('provider', form, lang)]: e.target.value })} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field">
            <div className="field-label">{L(STR.startDate, lang)}</div>
            <DateField className="field-input" lang={lang} value={form.startDate}
            max={form.endDate || undefined}
            onChange={(v) => setForm({ ...form, startDate: v })} />
          </div>
          <div className="field">
            <div className="field-label">{L(STR.endDate, lang)}</div>
            <DateField className="field-input" lang={lang} value={form.endDate}
            min={form.startDate || undefined}
            onChange={(v) => setForm({ ...form, endDate: v })} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field">
            <div className="field-label">{L(STR.points, lang)}</div>
            <input className="field-input" type="number" min="0" step="0.5"
            value={form.points}
            onChange={(e) => setForm({ ...form, points: e.target.value })} />
          </div>
          <div className="field" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            {form.endDate &&
            <div className="muted-2" style={{
              fontSize: 11, padding: '8px 10px',
              border: '0.5px solid var(--border)', borderRadius: 8,
              background: 'var(--surface)',
              display: 'flex', alignItems: 'center', gap: 6
            }}>
                <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: isInProgress ? 'var(--accent)' : 'var(--success)'
              }} />
                {isInProgress ?
              lang === 'zh' ? '進行中' : 'In progress' :
              lang === 'zh' ? '已完成' : 'Completed'}
              </div>
            }
          </div>
        </div>

        {/* Qualification expiry — the field that turns a filed certificate into
             a tracked credential (BLS/ACLS/PALS cards lapse every 2 years and
             nurses currently rely on memory). Most CNE records do NOT expire,
             so this stays behind a disclosure: it is pre-opened only when the
             record already carries an expiry, or when the title/category says
             this is the kind of card that lapses. Reminders at 90/30/7 days
             come from notify-cron's sweepCertExpiry. */}
        <ExpiryField
          lang={lang}
          value={form.expiresOn}
          minDate={form.endDate || undefined}
          suggest={recordLooksLikeCredential(record, form)}
          onChange={(v) => setForm({ ...form, expiresOn: v })} />

        {/* Editable attendance — tappable session pills + 編輯日期 dialog.
             Writes straight to course_attendance on each tap (independent of
             the record patch saved by 儲存), same as the 進行中 card. */}
        <div className="field">
          <div className="field-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{lang === 'zh' ? '出席情況' : 'Attendance'}</span>
            <span className="muted-2" style={{ fontSize: 10.5, letterSpacing: '0.04em' }}>
              {attended}/{total} · {total} {lang === 'zh' ? '節' : 'sessions'}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {sessions.map((s) =>
            <SessionPill
              key={s.idx}
              session={s}
              status={my[s.idx]}
              onCycle={() => setAtt(attKey, s.idx, NEXT_STATUS[my[s.idx]])} />
            )}
            <button
              type="button"
              onClick={() => setSessEditorOpen(true)}
              aria-label={lang === 'zh' ? '編輯日期' : 'Edit session dates'}
              title={lang === 'zh' ? '編輯日期' : 'Edit session dates'}
              style={{
                appearance: 'none', cursor: 'pointer',
                width: 30, height: 30, borderRadius: 999,
                background: 'var(--surface)',
                border: '0.5px solid var(--border)',
                color: 'var(--fg-2)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: 0
              }}>
              <Icon name="edit" size={13} stroke={1.7} />
            </button>
          </div>
          <div className="muted-2" style={{ fontSize: 10.5, marginTop: 8, letterSpacing: '0.02em' }}>
            {lang === 'zh' ? '點擊日期標記出席' : 'Tap a date to mark attendance'}
            {scheduleChanged &&
            <span style={{ marginLeft: 6, color: 'var(--accent)' }}>
                · {lang === 'zh' ? '自訂日期' : 'custom dates'}
              </span>
            }
          </div>
          {/* Contribute the edited schedule to the shared catalogue — framed as
               a correction so it routes through admin review, not a direct
               write. Catalog records only (see canShareSchedule). */}
          {canShareSchedule &&
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            style={{
              appearance: 'none', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              alignSelf: 'flex-start', marginTop: 8, padding: '6px 4px',
              background: 'transparent', border: 0,
              color: 'var(--accent)', font: 'inherit', fontSize: 12
            }}>
            <Icon name="upload" size={13} stroke={1.7} />
            {lang === 'zh' ? '課表有錯?回報正確時間到社群' : 'Schedule wrong? Suggest a correction'}
          </button>
          }
        </div>

        <div className="field">
          <div className="field-label">{L(STR.category, lang)}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['core', 'elective', 'specialty'].map((cat) =>
            <button key={cat} type="button"
            className={'filter-chip' + (form.category === cat ? ' on' : '')}
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={() => setForm({ ...form, category: cat })}>
                <CatDot cat={cat} size={6} /> {L(STR[cat], lang)}
              </button>
            )}
          </div>
        </div>
        <div className="field">
          <div className="field-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{lang === 'zh' ? '備註（選填）' : 'Notes (optional)'}</span>
            <span className="muted-2" style={{ fontSize: 10.5, letterSpacing: '0.04em' }}>
              {lang === 'zh' ?
              `圖片 ${totalImages}/3` :
              `Images ${totalImages}/3`}
            </span>
          </div>
          <textarea className="field-input" rows={3} value={form.notesText}
          style={{ resize: 'vertical', minHeight: 64, font: 'inherit' }}
          onChange={(e) => setForm({ ...form, notesText: e.target.value })}
          placeholder={lang === 'zh' ? '例：講者、主題、心得…' : 'Speaker, topic, takeaway…'} />

          {/* Image attachments — up to 3 photos / images on the notes. */}
          <input
            ref={noteImgRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={onPickNoteImages} />
          
          <div style={{
            display: 'flex', gap: 8, flexWrap: 'wrap',
            marginTop: 10
          }}>
            {existingImages.map((p) =>
            <div key={p} className="rx-note-img" style={{
              position: 'relative',
              width: 78, height: 78, borderRadius: 10, overflow: 'hidden',
              background: 'var(--surface-2)',
              border: '0.5px solid var(--border)'
            }}>
                {existingUrls[p] ?
              <img src={existingUrls[p]} alt="note image" style={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block'
              }} /> :
              // Shimmer while the signed URL resolves — reads as "loading",
              // not "awaiting upload" (the old static upload icon was misleading).
              <Skeleton w="100%" h="100%" r={0} />
              }
                <button type="button"
              onClick={() => removeExistingImage(p)}
              aria-label="Remove image"
              style={{
                position: 'absolute', top: 4, right: 4,
                width: 22, height: 22, borderRadius: '50%',
                background: 'rgba(0,0,0,0.7)', color: '#fff',
                border: 0, padding: 0, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                  <Icon name="close" size={11} stroke={2.4} />
                </button>
              </div>
            )}
            {newImages.map((img, i) =>
            <div key={`new-${i}`} style={{
              position: 'relative',
              width: 78, height: 78, borderRadius: 10, overflow: 'hidden',
              background: 'var(--surface-2)',
              border: '0.5px solid var(--accent)'
            }}>
                <img src={img.previewUrl} alt="" style={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block'
              }} />
                <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                color: '#fff', fontSize: 9.5, padding: '2px 4px',
                textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase'
              }}>
                  {lang === 'zh' ? '新' : 'New'}
                </div>
                <button type="button"
              onClick={() => removeNewImage(i)}
              aria-label="Remove image"
              style={{
                position: 'absolute', top: 4, right: 4,
                width: 22, height: 22, borderRadius: '50%',
                background: 'rgba(0,0,0,0.7)', color: '#fff',
                border: 0, padding: 0, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                  <Icon name="close" size={11} stroke={2.4} />
                </button>
              </div>
            )}
            {canAddMoreImages &&
            <button type="button"
            onClick={() => noteImgRef.current?.click()}
            style={{
              width: 78, height: 78, borderRadius: 10,
              background: 'transparent',
              border: '1px dashed var(--border-strong)',
              color: 'var(--fg-3)', cursor: 'pointer',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 4
            }}>
                <Icon name="plus" size={16} stroke={2} />
                <span style={{ fontSize: 10, letterSpacing: '0.06em' }}>
                  {lang === 'zh' ? '加入圖片' : 'Add image'}
                </span>
              </button>
            }
          </div>
          {imageUploadErr &&
          <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 6 }}>
              {imageUploadErr}
            </div>
          }
          <div className="muted-2" style={{ fontSize: 10.5, marginTop: 6, letterSpacing: '0.02em' }}>
            {lang === 'zh' ?
            '最多 3 張圖片 · JPG / PNG · 單張最大 5 MB' :
            'Up to 3 images · JPG / PNG · 5 MB each'}
          </div>
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          <button type="button" className="btn btn-ghost"
          style={{ flex: '0 0 auto', color: 'var(--danger)' }}
          onClick={() => setDelConfirm(true)}>
            <Icon name="close" size={14} stroke={2} />
            {lang === 'zh' ? '刪除' : 'Delete'}
          </button>
          {/* LoadingButton centralises the debounce: onClick is suppressed
              while busy AND a spinner replaces the icon. The form-invalid case
              stays gated by `disabled` so it never shows "Saving…". */}
          <LoadingButton type="button" className="btn btn-primary btn-fill"
          isLoading={busy}
          disabled={!form.title || !form.startDate || !form.endDate || form.endDate < form.startDate}
          loadingLabel={lang === 'zh' ? '儲存中…' : 'Saving…'}
          leftIcon={<Icon name="check" size={15} stroke={2.2} />}
          onClick={submit}>
            {lang === 'zh' ? '儲存' : 'Save'}
          </LoadingButton>
        </div>

        {/* Delete confirm lives at the sheet root — see <DeleteRecordConfirm>. */}

        {sessEditorOpen &&
        <SessionScheduleDialog
          course={courseForSessions}
          lang={lang}
          sessions={sessions}
          hasOverride={hasOverride}
          onClose={() => setSessEditorOpen(false)}
          onSave={(entries) => {saveSessions(entries);setSessEditorOpen(false);}}
          onReset={() => {resetSessionsToDefault();setSessEditorOpen(false);}} />
        }

        {/* Share-schedule shortcut → standard report sheet, preset to
             sessions_list and pre-filled with the user's edited schedule.
             Uses the real catalog course (real id + canonical sessions_list). */}
        {shareOpen && typeof ReportCourseSheet === 'function' && catalogCourse &&
        <ReportCourseSheet
          lang={lang}
          course={catalogCourse}
          initial={{
            fieldPath: 'sessions_list',
            reportType: shareCanonicalList ? 'incorrect_field' : 'missing_field',
            suggestedValue: JSON.stringify(shareOverrideList),
          }}
          onClose={() => setShareOpen(false)}
          onSubmitted={() => {}} />
        }
      </div>)
      }

      {/* Zoom overlay — full-bleed image / pdf preview, dismiss by tap. */}
      {zoomSrc &&
      <div
        role="dialog"
        aria-modal="true"
        onClick={() => setZoomSrc(null)}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.92)',
          zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20, cursor: 'zoom-out'
        }}>
        
          <button type="button"
        onClick={(e) => {e.stopPropagation();setZoomSrc(null);}}
        aria-label="Close"
        style={{
          position: 'absolute', top: 16, right: 16,
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(255,255,255,0.12)', color: '#fff',
          border: 0, padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)'
        }}>
            <Icon name="close" size={18} stroke={2} />
          </button>
          {zoomSrc.kind === 'pdf' ?
        <div onClick={(e) => e.stopPropagation()} style={{
          width: 'min(94vw, 900px)', height: 'min(86vh, 1100px)',
          background: '#fff', borderRadius: 8, overflow: 'hidden',
          cursor: 'default'
        }}>
              <iframe src={zoomSrc.src} title="verification pdf"
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }} />
            </div> :

        <img src={zoomSrc.src} alt="verification"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '94vw', maxHeight: '86vh',
          objectFit: 'contain', borderRadius: 6,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          cursor: 'default'
        }} />
        }
        </div>
      }

      {/* Delete confirm — ONE instance for both view and edit mode, rendered at
           the sheet root and portaled to <body> so it can never be pushed below
           the fold by whichever mode is showing. */}
      {delConfirm &&
      <DeleteRecordConfirm
        record={record}
        lang={lang}
        verifyCount={verifyCount}
        onDelete={onDelete}
        onCancel={() => setDelConfirm(false)}
        onDeleted={() => { setDelConfirm(false); onClose(); }} />
      }
    </Sheet>);

}

// ─────────────────────────────────────────────────────────────────────────────
// <StarRating> — the one star control, used for both the course and the
// provider so the two can never drift in size, semantics or accessibility.
//
// A radio group, not five buttons: screen readers announce "3 of 5" and the
// arrow keys work, which five independent <button>s do not give you. value=0
// means unrated — there is no zero-star rating and the server rejects one.
//
// The press animation uses motion-kit's spring (M.button + whileTap) rather
// than a bespoke CSS transition, so it feels like every other control in the
// app and inherits the prefers-reduced-motion handling already built there.
function StarRating({ value, onChange, lang, size = 30, label, disabled }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 2 }}>
      {stars.map(n => {
        const on = value >= n;
        return (
          <M.button key={n} type="button" role="radio" aria-checked={value === n}
            aria-label={lang === 'zh' ? `${n} 星` : `${n} star${n > 1 ? 's' : ''}`}
            disabled={disabled}
            onClick={() => onChange(n)}
            whileTap={disabled ? undefined : { scale: 0.82 }}
            whileHover={disabled ? undefined : { scale: 1.06 }}
            transition={{ duration: 0.16 }}
            style={{
              appearance: 'none', background: 'transparent', border: 0,
              padding: 2, cursor: disabled ? 'default' : 'pointer',
              lineHeight: 0, color: on ? 'var(--warning)' : 'var(--fg-3)',
              opacity: disabled ? 0.5 : 1,
            }}>
            <Icon name="star" size={size} stroke={1.6}
                  color={on ? 'var(--warning)' : 'currentColor'}
                  fill={on ? 'var(--warning)' : 'none'} />
          </M.button>
        );
      })}
    </div>
  );
}

// <ReviewChipRow> — the chips for one target at the current rating.
//
// ‼️ SELECTED CHIPS ARE PINNED FIRST AND NEVER SILENTLY DROPPED. Change the
// rating from 5★ to 2★ and "講者講得好" stays selected at the front even though
// it is no longer in the offered set — "體驗好但講師差" is a real combination,
// and deleting a chip she chose would look like the app editing her answer.
// reviewChipsFor() (data.js) does the pinning; the server accepts any chip at
// any rating so this cannot desync from what is storable.
//
// Mutual exclusion is applied through reviewToggleChip(): picking 講者一般
// clears 講者講得好, because they are the same question with opposite answers.
// The server enforces it too — chip counts are public.
function ReviewChipRow({ target, stars, selected, onToggle, lang, disabled }) {
  const chips = (typeof window.reviewChipsFor === 'function')
    ? window.reviewChipsFor(target, stars, selected) : [];
  if (!chips.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {chips.map(c => {
        const on = (selected || []).includes(c.key);
        return (
          <M.button key={c.key} type="button"
            className={'filter-chip' + (on ? ' on' : '')}
            aria-pressed={on} disabled={disabled}
            onClick={() => onToggle(c.key)}
            whileTap={disabled ? undefined : { scale: 0.94 }}
            transition={{ duration: 0.14 }}>
            {lang === 'zh' ? c.zh : c.en}
          </M.button>
        );
      })}
    </div>
  );
}

// <ReviewStatusLines> — what happened to each half of the review.
//
// ‼️ TWO LINES, NOT ONE BADGE. A nurse who rated 1★ and wrote nothing was being
// told "審批中", which is both wrong and alarming: it reads as though Vital is
// deciding whether to allow her rating. Stars are never moderated, so they are
// reported separately from the comment, and body_status 'none' says so plainly.
// The point is that she can see her 1★ counted even when a comment was rejected.
function ReviewStatusLines({ bodyStatus, hasBody, lang }) {
  const zh = lang === 'zh';
  const comment = !hasBody || bodyStatus === 'none'
    ? null
    : bodyStatus === 'published' ? (zh ? '已公開' : 'Published')
    : bodyStatus === 'rejected'  ? (zh ? '未能公開' : 'Not published')
    : (zh ? '審核中' : 'Awaiting review');
  return (
    <div style={{ fontSize: 11.5, lineHeight: 1.7, padding: '8px 10px',
                  borderRadius: 8, background: 'var(--bg-2)' }}>
      <div>⭐ {zh ? '評分：已計入' : 'Rating: counted'}</div>
      {comment && <div>💬 {zh ? '評語：' : 'Comment: '}{comment}</div>}
    </div>
  );
}

// <CourseReviewForm> — the review itself. Rendered inline by the
// completion-moment card and inside the sheet opened from 已完成紀錄, so both
// entry points ask exactly the same questions in the same order.
//
// ‼️ ONLY THE COURSE STARS ARE REQUIRED, and they save on the tap. Everything
// below is optional and progressively revealed. That is the whole design: the
// surface this replaced asked for two chips and got 1 use across 362 courses,
// while the 👍 one screen away got 51.
//
// ‼️ STARS AND CHIPS SAVE INSTANTLY; TEXT NEEDS AN EXPLICIT CONFIRM — and those
// go through DIFFERENT SERVER FUNCTIONS (saveCourseRating takes no body
// parameter at all). The confirm button is therefore a contract, not a UI
// convention: text cannot ride along with a rating save even from a crafted
// request. Autosaving the body on blur, which this used to do, also fired on
// every keyboard dismissal and re-queued the comment for moderation mid-thought.
function CourseReviewForm({ lang, courseId, onSaved, compact, onDirtyChange }) {
  const [courseStars, setCourseStars]     = React.useState(0);
  const [providerStars, setProviderStars] = React.useState(0);
  const [courseChips, setCourseChips]     = React.useState([]);
  const [providerChips, setProviderChips] = React.useState([]);
  const [body, setBody]           = React.useState('');
  const [savedBody, setSavedBody] = React.useState('');   // last persisted text
  const [bodyStatus, setBodyStatus] = React.useState('none');
  const [bodyBusy, setBodyBusy]   = React.useState(false);
  const [bodyJustSaved, setBodyJustSaved] = React.useState(false);
  const [busy, setBusy]     = React.useState(false);
  const [error, setError]   = React.useState(null);
  const [editable, setEditable] = React.useState(true);
  const [loaded, setLoaded] = React.useState(false);
  const [unavailable, setUnavailable] = React.useState(false);

  const dirty = body.trim() !== (savedBody || '').trim();
  // The host sheet needs to know there is unconfirmed text so closing can warn.
  React.useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, missing } = await window.DB.fetchMyCourseReview(courseId);
        if (!alive) return;
        if (missing) { setUnavailable(true); return; }
        if (data) {
          setCourseStars(Number(data.course_stars) || 0);
          setProviderStars(Number(data.provider_stars) || 0);
          setCourseChips(Array.isArray(data.course_chips) ? data.course_chips : []);
          setProviderChips(Array.isArray(data.provider_chips) ? data.provider_chips : []);
          setBody(data.body || '');
          setSavedBody(data.body || '');
          setBodyStatus(data.body_status || 'none');
          setEditable(data.editable !== false);
        }
      } catch (_) { /* best-effort: an unreachable backend shows a blank form */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [courseId]);

  const mapError = React.useCallback((err) => {
    const msg = String(err?.message || '');
    return msg.includes('attendance_required')
      ? (lang === 'zh' ? '要先喺紀錄標記為已完成先可以評價。'
                       : 'Mark the course completed in your records first.')
    // ‼️ NEVER let this fall through to the generic 「遲啲再試」 below. A course
    // whose occurrence identity is absent (never minted, or withheld by review)
    // will NOT start saving later, so inviting a retry is a promise the server
    // cannot keep. Same defect as the 👍 silent revert — see reactionOutcome()
    // in screens.jsx.
    : msg.includes('no_offering_identity') || msg.includes('no_series_identity')
      ? (lang === 'zh' ? '呢個課程目前未能接受評價。'
                       : "Ratings aren't available for this course.")
    : msg.includes('edit_window_closed')
      ? (lang === 'zh' ? '評價已過 30 日修改期。' : 'Reviews can only be changed within 30 days.')
    : msg.includes('author_suspended')
      ? (lang === 'zh' ? '你的評語功能暫時被停用。' : 'Comment posting is suspended on your account.')
    : msg.includes('chip_conflict')
      ? (lang === 'zh' ? '同一類標籤只可以揀一個。' : 'Only one chip per category.')
      : (lang === 'zh' ? '暫時儲存唔到,遲啲再試。' : "Couldn't save that — try again later.");
  }, [lang]);

  // Rating path — instant, and deliberately unable to carry text.
  const saveRating = React.useCallback(async (over) => {
    const next = {
      courseStars, providerStars: providerStars || null, courseChips, providerChips, ...over,
    };
    setBusy(true); setError(null);
    try {
      const { error: err, missing } = await window.DB.saveCourseRating(courseId, next);
      if (missing) { setUnavailable(true); return; }
      if (err) { setError(mapError(err)); return; }
      if (onSaved) onSaved(next);
    } catch (e) { setError(mapError(e)); }
    finally { setBusy(false); }
  }, [courseId, courseStars, providerStars, courseChips, providerChips, mapError, onSaved]);

  // Text path — only ever from the confirm button.
  const confirmBody = React.useCallback(async () => {
    if (bodyBusy || !dirty) return;
    setBodyBusy(true); setError(null);
    try {
      const { data, error: err, missing } = await window.DB.submitCourseReviewBody(courseId, body.trim());
      if (missing) { setUnavailable(true); return; }
      if (err) { setError(mapError(err)); return; }
      setSavedBody(body.trim());
      setBodyStatus((data && data.body_status) || (body.trim() ? 'pending' : 'none'));
      setBodyJustSaved(true);
      window.setTimeout(() => setBodyJustSaved(false), 2200);
      if (onSaved) onSaved({ body: body.trim() });
    } catch (e) { setError(mapError(e)); }
    finally { setBodyBusy(false); }
  }, [bodyBusy, dirty, courseId, body, mapError, onSaved]);

  const pickCourseStars = (n) => { setCourseStars(n); saveRating({ courseStars: n }); };

  const toggleChip = (which, key) => {
    const cur = which === 'course' ? courseChips : providerChips;
    const next = (typeof window.reviewToggleChip === 'function')
      ? window.reviewToggleChip(cur, key)
      : (cur.includes(key) ? cur.filter(k => k !== key) : cur.concat(key));
    if (which === 'course') setCourseChips(next); else setProviderChips(next);
    if (courseStars) saveRating(which === 'course' ? { courseChips: next } : { providerChips: next });
  };

  if (!loaded) return null;
  if (unavailable) {
    return (
      <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
        {lang === 'zh' ? '評價功能暫未開放。' : 'Reviews are not available yet.'}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 10 : 16 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          {lang === 'zh' ? '呢個課程幾多分?' : 'How would you rate this course?'}
        </div>
        <StarRating value={courseStars} onChange={pickCourseStars} lang={lang}
                    disabled={busy || !editable}
                    label={lang === 'zh' ? '課程評分' : 'Course rating'} />
      </div>

      {/* Everything below appears only once she has rated — asking for detail
          before the one required tap is what buries the whole surface. */}
      {courseStars > 0 && (
        <>
          <div>
            <div className="muted-2" style={{ fontSize: 11.5, marginBottom: 6 }}>
              {lang === 'zh' ? '有咩想補充?(可揀多過一個)' : 'Anything to add? (optional)'}
            </div>
            <ReviewChipRow target="course" stars={courseStars} selected={courseChips}
                           onToggle={(k) => toggleChip('course', k)}
                           lang={lang} disabled={busy || !editable} />
          </div>

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
              {lang === 'zh' ? '主辦機構呢?(選填)' : 'And the provider? (optional)'}
            </div>
            {/* Says out loud what is being rated, so a bad speaker does not get
                charged to the organisation's account. */}
            <div className="muted-2" style={{ fontSize: 11, lineHeight: 1.45, marginBottom: 6 }}>
              {lang === 'zh'
                ? '報名流程、證書幾時收到、場地同行政安排 — 唔係講者質素。'
                : 'Enrolment, certificate turnaround, venue and admin — not the teaching.'}
            </div>
            <StarRating value={providerStars}
                        onChange={(n) => { setProviderStars(n); saveRating({ providerStars: n }); }}
                        lang={lang} size={24} disabled={busy || !editable}
                        label={lang === 'zh' ? '機構評分' : 'Provider rating'} />
            <div style={{ marginTop: 8 }}>
              <ReviewChipRow target="provider" stars={providerStars || courseStars}
                             selected={providerChips}
                             onToggle={(k) => toggleChip('provider', k)}
                             lang={lang} disabled={busy || !editable} />
            </div>
          </div>

          {/* ── Free text — the only part a human reads before anyone else ── */}
          <div>
            <div className="muted-2" style={{ fontSize: 11.5, marginBottom: 6 }}>
              {lang === 'zh' ? '想寫低啲咩?(選填)' : 'Anything to write? (optional)'}
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 1000))}
              disabled={bodyBusy || !editable}
              rows={3}
              maxLength={1000}
              placeholder={lang === 'zh'
                ? '例如:講者用咗好多真實個案,對新入職嘅同事好有用。'
                : 'e.g. The speaker used a lot of real cases — useful for new staff.'}
              style={{ width: '100%', padding: 10, borderRadius: 10,
                       border: '1px solid var(--border)', resize: 'vertical', font: 'inherit',
                       background: 'var(--surface)' }} />
            {/* ‼️ The safety line comes BEFORE the button. Nurses write "上堂講咗
                我哋 Ward 某個病人…" without thinking, and that is a worse problem
                than anyone recognising the author. */}
            <div className="muted-2" style={{ fontSize: 10.5, lineHeight: 1.45, marginTop: 4 }}>
              {lang === 'zh'
                ? '請勿包含病人、同事或其他人士嘅姓名、聯絡資料或可識別個案資料。文字會經人手審核後先公開。'
                : 'Do not include names, contact details or identifiable case information about patients, colleagues or anyone else. Text is reviewed by a person before it is published.'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
              <M.button type="button" className="btn btn-primary"
                disabled={!dirty || bodyBusy || !editable}
                onClick={confirmBody}
                whileTap={(!dirty || bodyBusy) ? undefined : { scale: 0.97 }}
                style={{ opacity: (!dirty || bodyBusy || !editable) ? 0.5 : 1 }}>
                {bodyBusy
                  ? (lang === 'zh' ? '提交中…' : 'Submitting…')
                  : (lang === 'zh' ? '提交評語' : 'Submit comment')}
              </M.button>
              <span className="muted-2" style={{ fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                {body.length}/1000
              </span>
              <AnimatePresence>
                {bodyJustSaved && (
                  <M.span key="saved"
                    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
                    style={{ fontSize: 11.5, color: 'var(--success)',
                             display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="check" size={12} stroke={2.4} />
                    {lang === 'zh' ? '已提交,等待審核' : 'Submitted for review'}
                  </M.span>
                )}
              </AnimatePresence>
            </div>
            {dirty && !bodyBusy && (
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4, color: 'var(--warning)' }}>
                {lang === 'zh' ? '未提交 — 撳「提交評語」先會儲存。' : 'Not submitted yet — tap Submit comment to save.'}
              </div>
            )}
          </div>

          <ReviewStatusLines bodyStatus={bodyStatus} hasBody={!!savedBody} lang={lang} />

          {/* ‼️ NEVER THE WORD "匿名" ON ITS OWN. Vital withholds her name, but the
              words themselves are public and colleagues can recognise a voice.
              Saying only "anonymous" would be a promise the product cannot keep. */}
          <div className="muted-2" style={{ fontSize: 10.5, lineHeight: 1.45 }}>
            {lang === 'zh'
              ? 'Vital 唔會顯示你嘅姓名或帳戶資料。評分以統計顯示;評語經審核後會公開畀其他登入護士睇,所以認識你嘅人仍然有可能從內容認得出你。管理員可見。30 日內可以修改。'
              : 'Vital never shows your name or account. Ratings appear as aggregates; comments, once reviewed, are visible to other signed-in nurses — so people who know you may still recognise you from what you wrote. Visible to admins. Editable for 30 days.'}
          </div>
        </>
      )}

      {!editable && (
        <div className="muted-2" style={{ fontSize: 11 }}>
          {lang === 'zh' ? '已過 30 日修改期。' : 'The 30-day editing window has closed.'}
        </div>
      )}
      {error && <div style={{ fontSize: 11.5, color: 'var(--danger)' }}>{error}</div>}
    </div>
  );
}

// <ExperienceAskCard> — ONE question, ONE tap, at the moment it can be answered.
//
// ‼️ INLINE, NEVER A SHEET. app.jsx enforces one modal ask per session, ever
// (maybeOfferPush then maybeOfferReview, both fired from the same record-save
// moment). A third sheet here would not queue politely behind them — it would
// take the slot the App Store review triage needs, which is our only
// 5.6.1-compliant path to a rating. This card costs that budget nothing.
//
// ‼️ PROMINENT, NOT TIDIED AWAY. The same question used to live as a chip near
// the bottom of the course detail page, and the live catalogue showed what that
// placement earned it: 1 use of worth_it across 362 courses, against 51 for the
// 👍 in the sticky action bar. Same feature, same words, 51 : 1, decided by
// position alone. If this ends up folded into a collapsed row, expect that
// result again.
//
// ‼️ IT USED TO RENDER THE WHOLE REVIEW FORM — stars, chips, a submit flow — at
// the exact moment whose only advantage is that it costs one second. The
// completion moment is the cheapest attention this product will ever get and it
// was being spent on the most expensive control. One question. One tap. Done.
// The full review still exists, opened DELIBERATELY from a row in 已完成紀錄
// (CourseReviewSheet); the difference between a tap she chose and an ask we
// interrupted with is the whole reason that one may be a form and this may not.
//
// ‼️ ONE POSITIVE ACTION, AND NO 「一般」. `worth_it` is a toggle: the row exists
// or it does not, and its absence means UNKNOWN, not "not worth it" — there is
// no negative representation for it anywhere in the schema. A second button
// would therefore either write nothing while implying it recorded something, or
// smuggle in a semantic the database cannot express and the rollups cannot read.
// If negative experience evidence is ever wanted, it needs its own reaction type
// and its own decision, not a button that quietly means nothing.
function ExperienceAskCard({ lang, courseId, title, onToggleReaction, onDismiss }) {
  // ‼️ THE TOGGLE CUTS BOTH WAYS, so the card must know her current state before
  // it offers a control. toggle_course_reaction DELETES an existing reaction —
  // a nurse who logs the same catalogue course twice would tap 👍 expecting to
  // record support and instead REMOVE the support she gave last time, while the
  // card thanked her for it. Read first, then decide whether to ask at all.
  const [phase, setPhase] = React.useState('loading');   // loading | ask | saving | done | hidden
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!courseId) { setPhase('hidden'); return; }
      const read = window.DB?.getCourseReactionSummary;
      if (typeof read !== 'function') { setPhase('ask'); return; }
      const { data } = await read(courseId);
      if (!alive) return;
      // Already answered: say nothing. Re-asking someone who has already given
      // this signal is a nag, and tapping would withdraw it.
      const mine = new Set((data && data.my_reactions) || []);
      setPhase(mine.has('worth_it') ? 'hidden' : 'ask');
    })();
    return () => { alive = false; };
  }, [courseId]);

  if (phase === 'hidden' || phase === 'loading') return null;

  const react = async () => {
    if (phase !== 'ask') return;
    setPhase('saving');
    const res = typeof onToggleReaction === 'function'
      ? await onToggleReaction(courseId, 'worth_it') : { error: true };
    // `deferred` means the auth flow took over; leave the card as it was so the
    // tap still works once she is back.
    if (res && res.deferred) { setPhase('ask'); return; }
    // ‼️ EVERY FAILURE HERE IS A GATE SHE CANNOT ACT ON — attendance_required,
    // no_series_identity, unknown_course. They all mean the card should never
    // have been offered. Showing an error would be noise about a control that
    // was our mistake to render, so it simply goes away (the same degradation
    // getCourseContributors uses when attribution cannot load).
    if (!res || res.error) { console.warn('worth_it ask failed', res && res.error); setPhase('hidden'); return; }
    setPhase('done');
  };

  return (
    <div style={{
      border: '0.5px solid var(--border)', borderRadius: 14,
      padding: '14px 14px 12px', background: 'var(--bg-1)',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>
            {phase === 'done'
              ? (lang === 'zh' ? '已記低,多謝你' : 'Saved — thank you')
              : (lang === 'zh' ? '值得推薦俾其他護士嗎?' : 'Worth recommending to other nurses?')}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--fg-2)' }}>
            {phase === 'done'
              ? (lang === 'zh' ? '你嘅答案會幫其他護士揀課程。' : 'Your answer helps other nurses choose.')
              : (lang === 'zh'
                ? <>你啱啱完成咗<strong>{title}</strong>。撳一下就得。</>
                : <>You just completed <strong>{title}</strong>. One tap.</>)}
          </div>
        </div>
        <button type="button" onClick={onDismiss}
          aria-label={lang === 'zh' ? '關閉' : 'Dismiss'}
          style={{ appearance: 'none', background: 'transparent', border: 0,
                   padding: 4, cursor: 'pointer', color: 'var(--fg-3)', flexShrink: 0 }}>
          {/* 'close', not 'x' — Icon's default case is a small circle, so a
              wrong name renders a meaningless dot instead of failing. */}
          <Icon name="close" size={15} stroke={2} />
        </button>
      </div>

      {phase !== 'done' && (
        <button type="button" onClick={react} disabled={phase === 'saving'}
          className="btn btn-primary"
          style={{ alignSelf: 'flex-start', fontSize: 14, padding: '9px 18px' }}>
          {lang === 'zh' ? '👍 值得上' : '👍 Worth it'}
        </button>
      )}
    </div>
  );
}

// <CourseReviewSheet> — the second entry point, opened deliberately from a row
// in 已完成紀錄. A Sheet is fine HERE and not at the completion moment: the
// one-ask-per-session budget in app.jsx governs UNSOLICITED asks, and this one
// is a tap the nurse chose to make.
function CourseReviewSheet({ open, onClose, lang, courseId, title, onSaved }) {
  // ‼️ UNCONFIRMED TEXT MUST NOT VANISH SILENTLY. Stars and chips are already
  // saved by the time she closes, but a typed-but-unsubmitted comment exists
  // only in this component. Discarding it without a word is the one outcome
  // that reads as a bug — so closing while dirty asks first. Deliberately NOT a
  // hidden local draft: text that reappears days later is more surprising than
  // text that was clearly discarded.
  const [dirty, setDirty] = React.useState(false);
  const [confirmClose, setConfirmClose] = React.useState(false);
  // Hooks run before the early return so the hook count never changes.
  if (!open) return null;

  const attemptClose = () => { if (dirty) setConfirmClose(true); else onClose(); };

  return (
    <Sheet open={true} onClose={attemptClose} title={lang === 'zh' ? '評價課程' : 'Rate this course'}>
      <div style={{ padding: '4px 20px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.4 }}>{title}</div>
        <CourseReviewForm lang={lang} courseId={courseId} onSaved={onSaved}
                          onDirtyChange={setDirty} />
      </div>

      {confirmClose && (
        <div role="alertdialog" aria-modal="true"
             style={{ position: 'absolute', inset: 0, zIndex: 40, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', padding: 24,
                      background: 'color-mix(in oklch, var(--bg) 72%, transparent)' }}>
          <div className="card" style={{ maxWidth: 320, display: 'flex',
                                         flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {lang === 'zh' ? '放棄未提交嘅評語?' : 'Discard your unsubmitted comment?'}
            </div>
            <div className="muted-2" style={{ fontSize: 12, lineHeight: 1.5 }}>
              {lang === 'zh'
                ? '你嘅星級同標籤已經儲存咗。未撳「提交評語」嘅文字唔會保留。'
                : 'Your rating and chips are already saved. Text you did not submit will not be kept.'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }}
                onClick={() => setConfirmClose(false)}>
                {lang === 'zh' ? '繼續編輯' : 'Keep editing'}
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }}
                onClick={() => { setConfirmClose(false); onClose(); }}>
                {lang === 'zh' ? '放棄' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Sheet>
  );
}

Object.assign(window, {
  parseSessions,
  useAttendance,
  hydrateAttendance,
  clearAttendanceCache,
  SessionPill,
  ExperienceAskCard,
  // Review surfaces. ‼️ Every name consumed by screens.jsx MUST be listed here —
  // screens.jsx calls them behind `typeof x === 'function'` guards, so a missing
  // export renders nothing at all instead of throwing.
  StarRating,
  ReviewChipRow,
  ReviewStatusLines,
  CourseReviewForm,
  CourseReviewSheet,
  InProgressCourseCard,
  InProgressSection,
  PendingVerificationSection,
  RecordDetailSheet,
  // Note-blob format + completion-flag helpers (screens.jsx + handlers
  // outside this file use these so the COMPLETED:1 marker stays in sync).
  // ‼️ Every marker predicate MUST be listed here. screens.jsx calls them
  // behind `typeof x === 'function'` guards, so a missing name doesn't throw —
  // the surface just silently never renders, which for isRecordPointsPending
  // means a 0-point record aging into 已完成 with no warning at all.
  parseNoteBlob,
  buildNoteBlob,
  isRecordCompleted,
  isRecordPointsPending,
  // Used by the optional Pro audit-pack export to keep appended certificate
  // images compact before the native print bridge receives the document.
  compressImage,
  // Shared local-date helpers (screens.jsx reuses these so the in-progress
  // ↔ completed split uses the same notion of "today" everywhere).
  ymdLocal,
  todayLocalIso,
  localDateFromIso
});

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { ymdLocal, todayLocalIso, localDateFromIso, parseSessions, ATT_KEY, readAttendance, writeAttendance, currentUserId, currentLang, showAttendanceError, useAttendance, SESSIONS_KEY, readCustomSessions, writeCustomSessions, useCustomSessions, hydrateAttendance, clearAttendanceCache, normaliseSessionEntry, serialiseSessionEntry, parseTimeTextStart, deriveScheduleFields, summariseSessionList, sameSchedule, resolveSessions, NEXT_STATUS, SessionPill, InProgressCourseCard, CourseActionPickerDialog, SessionScheduleDialog, manualRecordToPseudoCourse, CALENDAR_OPEN_KEY, UpcomingCalendarSection, InProgressSection, PendingVerificationSection, parseNoteBlob, buildNoteBlob, isRecordCompleted, isRecordPointsPending, compressImage, CREDENTIAL_TITLE_RE, recordLooksLikeCredential, ExpiryField, DeleteRecordConfirm, editedSourceField, RecordDetailSheet, StarRating, ReviewChipRow, ReviewStatusLines, CourseReviewForm, ExperienceAskCard, CourseReviewSheet });
